import React, { useState, useRef } from "react";
import TradeEditModal from "./TradeEditModal";
import { generatePDFFromElement, blobToBase64 } from "../utils/pdfGenerator";
import {
  formatCurrency,
  formatDate,
  getEFTTypeDisplayName,
  filterCommissionTrustActivityEFTs,
  getPaymentPlanDisplayName,
  isReversalLedgerEftRef,
  resolveCommissionTrustDisplayEftNumber,
  formatCommissionTrustActivityEftNumber,
} from "../utils/eftUtils";
import axiosInstance from "../config/axios";
import { DEFAULT_TAX_RATE, INCLUSIVE_HST_FRACTION } from "../constants/tax";
import { useRole } from "../context/RoleContext";
import { isAgentRecordLocked } from "../utils/appAuth";
import WorkflowBadge from "./WorkflowBadge";
import {
  formatPercentDisplayForSheet,
  roundTo2,
  roundTo2Str,
} from "../utils/numberUtils";
import {
  getBuyerRebateListingSellingForSheet,
  isBuyerRebateIncludedYes,
} from "../utils/tradeFinancialUtils";
import {
  officeExpensesBaseForAgent,
  officeExpensesTaxForAgent,
  officeExpensesTotalForAgent,
  buildOfficeExpenseFinancialRows,
  addOfficeExpensesToNetToOfficeValues,
  getAgentFinancialTotalsBeforeOfficeExp,
} from "../utils/officeExpenses";
import EFTPaymentTables from "./TradeDetailsModal/EFTPaymentTables";
import DropboxFolderBrowser from "./TradeDetailsModal/DropboxFolderBrowser";
import { useDropbox } from "./TradeDetailsModal/useDropbox";
import * as DropboxHandlers from "./TradeDetailsModal/DropboxHandlers";
import TrsSignatureActions from "./TradeDetailsModal/TrsSignatureActions";
const TradeDetailsModal = ({ trade, onClose, onUpdate }) => {
  const { role } = useRole();
  const locked = isAgentRecordLocked(trade, role);
  const [isEditing, setIsEditing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [lastGeneratedPDF, setLastGeneratedPDF] = useState(null);
  const pdfContentRef = useRef();
  const [showPrintAgentModal, setShowPrintAgentModal] = useState(false);
  const {
    dropboxAccessToken,
    dropboxRefreshToken,
    setDropboxAccessToken,
    setDropboxRefreshToken,
    showFolderBrowser,
    setShowFolderBrowser,
    dropboxFolders,
    setDropboxFolders,
    currentPath,
    setCurrentPath,
    selectedFolder,
    setSelectedFolder,
    loadingFolders,
    setLoadingFolders,
    selectedFile,
    setSelectedFile,
    isUploadingToDropbox,
    setIsUploadingToDropbox,
  } = useDropbox();
  if (!trade) return null;
  const get = (obj, path, fallback = "-") =>
    path
      .split(".")
      .reduce((o, k) => (o && o[k] !== undefined ? o[k] : fallback), obj);
  const renderTable = (rows) => {
    if (!rows || !rows.length)
      return <div className="text-gray-500">No data.</div>;
    const headers = Object.keys(rows[0]);
    const capitalizedHeaders = headers.map(
      (h) => h.charAt(0).toUpperCase() + h.slice(1),
    );
    return (
      <table className="min-w-full border border-gray-300 mb-2 text-sm">
        <thead>
          <tr>
            {headers.map((h, index) => (
              <th key={h} className="px-2 py-1 text-left bg-gray-100 border">
                {capitalizedHeaders[index]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {headers.map((h) => (
                <td key={h} className="border px-2 py-1">
                  {row[h]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };
  const displayAmount = (val) =>
    val !== undefined && val !== null && val !== "" ? val : "0.00";
  const generateTradeHTML = async () => {
    const currentDate = new Date().toLocaleDateString();
    let realEstateTrustEFTs = [];
    let commissionTrustEFTs = [];
    if (trade && trade._id) {
      try {
        const [realEstateRes, commissionRes] = await Promise.all([
          axiosInstance.get(`/real-estate-trust-eft/trade/${trade._id}`),
          axiosInstance.get(`/commission-trust-eft/trade/${trade._id}`),
        ]);
        realEstateTrustEFTs = realEstateRes.data || [];
        commissionTrustEFTs = commissionRes.data || [];
      } catch (err) {
        console.error("Error fetching EFTs:", err);
      }
    }
    const transactionDetails = getTransactionDetails();
    return transactionDetails;
  };
  const handlePrint = async (selectedAgentIndex = null) => {
    if (isPrinting) return;
    setIsPrinting(true);
    try {
      // Try to open a new window first
      let printWindow = window.open("", "_blank");
      let useCurrentWindow = false;
      if (!printWindow) {
        // If popup is blocked, use current window
        useCurrentWindow = true;
        printWindow = window;
      }
      const currentDate = new Date().toLocaleDateString();
      // Fetch EFTs for print preview
      let realEstateTrustEFTs = [];
      let commissionTrustEFTs = [];
      let eftReversals = [];
      let commissionRecalls = [];
      let ledgerEntries = [];
      let agentYTDEfts = []; // All agent commission EFTs for YTD calculation
      let agentInfoForYTD = null; // Agent info for fee calculation
      if (trade && trade._id) {
        try {
          const [
            realEstateRes,
            commissionRes,
            reversalsRes,
            recallsRes,
            ledgerRes,
          ] = await Promise.all([
            axiosInstance.get(`/real-estate-trust-eft/trade/${trade._id}`),
            axiosInstance.get(`/commission-trust-eft/trade/${trade._id}`),
            axiosInstance.get(`/eft-reversals`, {
              params: { tradeId: trade._id },
            }),
            axiosInstance.get(`/commission-recalls`, {
              params: { tradeId: trade._id },
            }),
            axiosInstance.get("/ledger"),
          ]);
          realEstateTrustEFTs = realEstateRes.data || [];
          commissionTrustEFTs = commissionRes.data || [];
          eftReversals = reversalsRes.data || [];
          commissionRecalls = recallsRes.data || [];
          // Fetch all commission trust EFTs for YTD calculation (not just for this trade)
          const agentId = trade.agentCommissionList?.[0]?.agentId;
          const agentName = trade.agentCommissionList?.[0]?.agentName;
          if (agentId || agentName) {
            try {
              // Fetch with populated trade data, agent info, and all trades
              const [allEFTsRes, agentsRes, tradesRes] = await Promise.all([
                axiosInstance.get("/commission-trust-eft"),
                axiosInstance.get("/agents"),
                axiosInstance.get("/trades"),
              ]);
              const allCommissionEFTs = allEFTsRes.data || [];
              const agents = agentsRes.data || [];
              const allTrades = tradesRes.data || [];
              // Find agent info for fee calculation
              agentInfoForYTD = agents.find(
                (a) =>
                  a.employeeNo?.toString() === agentId || a._id === agentId,
              );
              // Get current trade numbe
              const currentTradeNumber = parseInt(
                trade.tradeNumber || trade.keyInfo?.tradeNumber || 0,
              );
              // Get the year from the current trade's finalized date for YTD calculation
              // YTD runs from Jan 1 to Dec 31 of the trade's finalized year, resetting on Jan 1st
              const currentTradeFinalizedDate = trade.keyInfo?.finalizedDate
                ? new Date(trade.keyInfo.finalizedDate)
                : null;
              const currentYear = currentTradeFinalizedDate
                ? currentTradeFinalizedDate.getFullYear()
                : new Date().getFullYear();
              // Filter: include all finalized trades for this agent with trade number <= current trade number
              agentYTDEfts = allCommissionEFTs
                .filter((eft) => {
                  if (eft.type !== "AgentCommissionTransfer") return false;
                  const matchesAgent =
                    eft.agentId === agentId || eft.agentName === agentName;
                  if (!matchesAgent) return false;
                  // Find the trade for this EFT
                  const eftTradeId = eft.tradeId?._id || eft.tradeId;
                  const eftTrade = allTrades.find((t) => t._id === eftTradeId);
                  if (!eftTrade || !eftTrade.isFinalized) return false;
                  // Get trade number and check if it's <= current trade number
                  const eftTradeNumber = parseInt(
                    eftTrade.tradeNumber || eftTrade.keyInfo?.tradeNumber || 0,
                  );
                  // Check if finalized in the same year as the current trade
                  const eftFinalizedDate = eftTrade.keyInfo?.finalizedDate
                    ? new Date(eftTrade.keyInfo.finalizedDate)
                    : null;
                  const isInSameYear =
                    eftFinalizedDate &&
                    eftFinalizedDate.getFullYear() === currentYear;
                  return isInSameYear && eftTradeNumber <= currentTradeNumber;
                })
                .map((eft) => {
                  // Attach full trade data to each EFT for buyer rebate calculation
                  const tradeData = allTrades.find(
                    (t) => t._id === eft.tradeId || t._id === eft.tradeId?._id,
                  );
                  return { ...eft, tradeData };
                });
            } catch (err) {
              console.error("Error fetching YTD EFTs:", err);
            }
          }
          // Get ledger entries and filter for this trade
          const allLedgerEntries = ledgerRes.data || [];
          const tradeNumber = trade.tradeNumber || trade.keyInfo?.tradeNumber;
          ledgerEntries = allLedgerEntries.filter(
            (entry) =>
              entry.tradeNumber === tradeNumber ||
              (entry.description &&
                entry.description.includes(`Trade #: ${tradeNumber}`)),
          );
        } catch (err) {
          // fallback to empty arrays
        }
      }
      const builderRepaymentEFTs = (commissionTrustEFTs || []).filter(
        (eft) => eft.type === "BuilderRepayment",
      );
      const generalToCommissionEFTs = (commissionTrustEFTs || []).filter(
        (eft) => eft.type === "GeneralToCommissionTransfer",
      );
      const commissionIncome =
        trade.commission?.commissionIncomeRows?.[0] || {};
      const baseOfficeCommission = "";
      // Get the trade classification
      const tradeClassification = trade.keyInfo?.classification || "";
      const isCoOperatingSide = tradeClassification === "CO-OPERATING SIDE";
      const taxRate =
        trade.customTaxRate != null ? trade.customTaxRate : DEFAULT_TAX_RATE;
      // Calculate Sub-Total and HST for Commission row
      const listingAmount =
        commissionIncome.listingAmount && commissionIncome.listingAmount !== ""
          ? parseFloat(commissionIncome.listingAmount)
          : 0;
      const sellingAmount =
        commissionIncome.sellingAmount && commissionIncome.sellingAmount !== ""
          ? parseFloat(commissionIncome.sellingAmount)
          : 0;
      const subTotal = (listingAmount + sellingAmount).toFixed(2);
      // Calculate HST based on the amounts (use trade's set tax rate when applied)
      const listingTax = (listingAmount * taxRate).toFixed(2);
      const sellingTax = (sellingAmount * taxRate).toFixed(2);
      const hst = (parseFloat(listingTax) + parseFloat(sellingTax)).toFixed(2);
      const total = (parseFloat(subTotal) + parseFloat(hst)).toFixed(2);
      // Get first outside broker row (from commission or outsideBrokers)
      let outsideBrokerInfo = null;
      if (trade.commission?.outsideBrokersRows?.length > 0) {
        outsideBrokerInfo = trade.commission.outsideBrokersRows[0];
      } else if (trade.outsideBrokers?.length > 0) {
        outsideBrokerInfo = trade.outsideBrokers[0];
      }
      // Determine side and amounts
      let obListing = "0.00",
        obSelling = "0.00",
        obSubTotal = "0.00",
        obTax = "0.00",
        obTotal = "0.00",
        obName = "";
      if (outsideBrokerInfo) {
        // Get the broker name
        const brokerName = `${
          outsideBrokerInfo.agentName || outsideBrokerInfo.firstName || ""
        } ${outsideBrokerInfo.lastName || ""}`.trim();
        // Get the company name
        const companyName =
          outsideBrokerInfo.company || outsideBrokerInfo.brokerage || "";
        // Combine name and company
        obName =
          brokerName && companyName
            ? `${brokerName} - ${companyName}`
            : brokerName || companyName || "Outside Broker";
        // Get the selling amount from either source
        const brokerSellingAmount = outsideBrokerInfo.sellingAmount || "0.00";
        // First check the broker's end from outsideBrokers array
        const matchingBrokerInOB = trade.outsideBrokers?.find((ob) => {
          const outsideBrokerName = `${ob.firstName} ${ob.lastName}`.trim();
          return (
            outsideBrokerName ===
              (outsideBrokerInfo.agentName || outsideBrokerInfo.firstName) ||
            ob.company === outsideBrokerInfo.company
          );
        });
        // Determine the end type from either the matching broker or current broker info
        let endType = "";
        if (matchingBrokerInOB) {
          if (
            matchingBrokerInOB.end?.toLowerCase().includes("listing") ||
            matchingBrokerInOB.type?.toLowerCase().includes("listing")
          ) {
            endType = "listing";
          } else {
            endType = "selling";
          }
        } else {
          const end = (
            outsideBrokerInfo.end ||
            outsideBrokerInfo.type ||
            ""
          ).toLowerCase();
          endType = end.includes("listing") ? "listing" : "selling";
        }
        // Place amount in appropriate column based on end type
        if (endType === "listing") {
          obListing = brokerSellingAmount;
          obSelling = "0.00";
        } else {
          obListing = "0.00";
          obSelling = brokerSellingAmount;
        }
        obSubTotal = brokerSellingAmount;
        obTax = outsideBrokerInfo.tax || "0.00";
        obTotal = outsideBrokerInfo.total || "0.00";
      }
      // Agent double ender: both sides in-house — show $0 on Outside Broker row; base office uses full commission
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        obListing = "0.00";
        obSelling = "0.00";
        obSubTotal = "0.00";
        obTax = "0.00";
        obTotal = "0.00";
      }
      // Referral agents (from Agent Info) appear under Expenses and reduce Base Office Commission
      const referralForExpenses = (trade.agentCommissionList || []).filter(
        (a) => (a.classification || "").toUpperCase() === "REFERRAL",
      );
      const referralAmountSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.amount || 0),
        0,
      );
      const referralTaxSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.tax || 0),
        0,
      );
      const referralTotalSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.total || 0),
        0,
      );
      const expensesRowsForPrint = [];
      if (outsideBrokerInfo) {
        expensesRowsForPrint.push({
          name: obName,
          listing: obListing,
          selling: obSelling,
          subTotal: obSubTotal,
          tax: obTax,
          total: obTotal,
        });
      }
      referralForExpenses.forEach((a) => {
        expensesRowsForPrint.push({
          name: `${a.agentName || ""} - Referral`,
          listing: "0.00",
          selling: a.amount || "0",
          subTotal: a.amount || "0",
          tax: a.tax || "0",
          total: a.total || "0",
        });
      });
      // Calculate Base Office Commission
      let baseOfficeCommissionListing,
        baseOfficeCommissionSelling,
        baseOfficeCommissionSubTotal,
        baseOfficeCommissionHST,
        baseOfficeCommissionTotal;
      if (isCoOperatingSide) {
        // For Co-operating Side, base office commission = commission (same values) unless there are referral agents (then deduct referral)
        if (referralForExpenses.length === 0) {
          baseOfficeCommissionListing = listingAmount;
          baseOfficeCommissionSelling = sellingAmount;
          baseOfficeCommissionSubTotal = subTotal;
          baseOfficeCommissionHST = hst;
          baseOfficeCommissionTotal = total;
        } else {
          baseOfficeCommissionListing = listingAmount;
          baseOfficeCommissionSelling = (
            parseFloat(sellingAmount) - referralAmountSum
          ).toFixed(2);
          baseOfficeCommissionSubTotal = (
            parseFloat(subTotal) - referralAmountSum
          ).toFixed(2);
          baseOfficeCommissionHST = (parseFloat(hst) - referralTaxSum).toFixed(
            2,
          );
          baseOfficeCommissionTotal = (
            parseFloat(total) - referralTotalSum
          ).toFixed(2);
        }
      } else {
        // For other classifications, base office commission is the net after outside broker + referral deductions
        baseOfficeCommissionListing = (
          parseFloat(listingAmount) - parseFloat(obListing)
        ).toFixed(2);
        baseOfficeCommissionSelling = (
          parseFloat(sellingAmount) -
          parseFloat(obSelling) -
          referralAmountSum
        ).toFixed(2);
        baseOfficeCommissionSubTotal = (
          parseFloat(subTotal) -
          parseFloat(obSubTotal) -
          referralAmountSum
        ).toFixed(2);
        baseOfficeCommissionHST = (
          parseFloat(hst) -
          parseFloat(obTax) -
          referralTaxSum
        ).toFixed(2);
        baseOfficeCommissionTotal = (
          parseFloat(total) -
          parseFloat(obTotal) -
          referralTotalSum
        ).toFixed(2);
      }
      // Calculate Agent Commission for all agents
      // For AGENT DOUBLE ENDER, track transaction order per agent
      const agentDoubleEnderOrder = new Map();
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        (trade.agentCommissionList || []).forEach((a, index) => {
          if (a.classification === "AGENT DOUBLE ENDER" && a.agentId) {
            const key = a.agentId;
            if (!agentDoubleEnderOrder.has(key)) {
              agentDoubleEnderOrder.set(key, []);
            }
            agentDoubleEnderOrder.get(key).push(index);
          }
        });
      }
      const agentCalculations = (trade.agentCommissionList || []).map(
        (agent, agentIndex) => {
          const agentAmount =
            agent.amount && agent.amount !== "" ? parseFloat(agent.amount) : 0;
          const agentFeesDeducted =
            agent.feesDeducted && agent.feesDeducted !== ""
              ? parseFloat(agent.feesDeducted)
              : 0;
          const agentClassification = agent.classification?.toLowerCase() || "";
          const buyerRebateAmount =
            isBuyerRebateIncludedYes(agent) && agent.buyerRebateAmount
              ? parseFloat(agent.buyerRebateAmount) || 0
              : 0;
          const shareAfterPlan = agentAmount - agentFeesDeducted;
          const displaySubForSheet = Math.max(
            0,
            shareAfterPlan - buyerRebateAmount,
          );
          // Determine agent amounts based on trade classification (not agent classification)
          let agentListing = "0.00";
          let agentSelling = "0.00";
          // For AGENT DOUBLE ENDER: use transactionType to determine which column
          if (
            tradeClassification === "AGENT DOUBLE ENDER" &&
            agent.classification === "AGENT DOUBLE ENDER"
          ) {
            // Determine transaction type - use saved value or infer from order
            let transactionType = agent.transactionType;
            // If transactionType is missing, infer from order
            if (!transactionType && agent.agentId) {
              const agentIndices =
                agentDoubleEnderOrder.get(agent.agentId) || [];
              const positionInList = agentIndices.indexOf(agentIndex);
              // First occurrence (position 0) = LISTING, second (position 1) = SELLING
              transactionType = positionInList === 0 ? "LISTING" : "SELLING";
            }
            if (transactionType === "LISTING") {
              // First transaction goes to Listing column
              agentListing = displaySubForSheet.toFixed(2);
              agentSelling = "0.00";
            } else if (transactionType === "SELLING") {
              // Second transaction goes to Selling column
              agentListing = "0.00";
              agentSelling = displaySubForSheet.toFixed(2);
            }
          } else if (isCoOperatingSide) {
            // For CO-OPERATING SIDE: put agent amount in selling column
            agentListing = "0.00";
            agentSelling = displaySubForSheet.toFixed(2);
          } else {
            // For LISTING SIDE: put agent amount in listing column
            agentListing = displaySubForSheet.toFixed(2);
            agentSelling = "0.00";
          }
          // Sub-Total column; Total / HST match Agent Info (net = amount + tax − totalFees).
          let agentSubTotal = (
            parseFloat(agentListing) + parseFloat(agentSelling)
          ).toFixed(2);
          const { agentTotal, agentHST } =
            getAgentFinancialTotalsBeforeOfficeExp(
              agent,
              parseFloat(agentSubTotal),
              taxRate,
              buyerRebateAmount,
            );
          const officeExpenseFinancialRows = buildOfficeExpenseFinancialRows(
            agent,
            tradeClassification,
            taxRate,
          );
          return {
            ...agent,
            agentListing,
            agentSelling,
            agentSubTotal,
            agentHST: agentHST.toFixed(2),
            agentTotal: agentTotal.toFixed(2),
            officeExpenseFinancialRows,
          };
        },
      );
      const showBaseOfficeAndNetToOffice =
        selectedAgentIndex === null || selectedAgentIndex === undefined;
      // Modal passes index into non-referral agents only; match by that list (by name/identity), not by order in full list.
      const nonReferralAgents = agentCalculations.filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      let agentCalculationsFiltered = agentCalculations;
      if (!showBaseOfficeAndNetToOffice && selectedAgentIndex != null) {
        const indices = Array.isArray(selectedAgentIndex)
          ? selectedAgentIndex
          : [selectedAgentIndex];
        const selected = indices
          .filter((i) => Number.isInteger(i) && nonReferralAgents[i])
          .map((i) => nonReferralAgents[i]);
        if (selected.length > 0) {
          agentCalculationsFiltered = selected;
        }
      }
      // Exclude referral agents from Net to Office agent sum (referral already deducted in Base Office Commission).
      // Net to Office: sub-total + HST on net sub-total (sheet Total excludes rebate add-back; EFT adds rebates separately).
      const agentsForNetToOffice = agentCalculationsFiltered.filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      const agentKeysForBuyerRebateSheet = showBaseOfficeAndNetToOffice
        ? null
        : new Set(
            agentsForNetToOffice.map((a) =>
              String(a.agentId ?? a.agentName ?? ""),
            ),
          );
      const buyerRebateCols = getBuyerRebateListingSellingForSheet(trade, {
        agentKeys: agentKeysForBuyerRebateSheet,
      });
      const buyerRebateListing = buyerRebateCols.listing.toFixed(2);
      const buyerRebateSelling = buyerRebateCols.selling.toFixed(2);
      const totalBuyerRebate = parseFloat(
        (buyerRebateCols.listing + buyerRebateCols.selling).toFixed(2),
      );
      let netToOfficeListing,
        netToOfficeSelling,
        netToOfficeSubTotal,
        netToOfficeHST,
        netToOfficeTotal;
      let totalAgentListing,
        totalAgentSelling,
        totalAgentSubTotal,
        totalAgentHST,
        totalAgentTotal;
      if (!isCoOperatingSide && agentsForNetToOffice.length === 2) {
        const agent1 = agentsForNetToOffice[0] || {};
        const agent2 = agentsForNetToOffice[1] || {};
        totalAgentListing =
          parseFloat(agent1.agentListing || 0) -
          parseFloat(agent2.agentListing || 0);
        totalAgentSelling =
          parseFloat(agent1.agentSelling || 0) -
          parseFloat(agent2.agentSelling || 0);
        totalAgentSubTotal =
          parseFloat(agent1.agentSubTotal || 0) -
          parseFloat(agent2.agentSubTotal || 0);
        totalAgentHST =
          parseFloat(agent1.agentHST || 0) - parseFloat(agent2.agentHST || 0);
        totalAgentTotal =
          parseFloat(agent1.agentTotal || 0) -
          parseFloat(agent2.agentTotal || 0);
      } else {
        totalAgentListing = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentListing || 0),
          0,
        );
        totalAgentSelling = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentSelling || 0),
          0,
        );
        totalAgentSubTotal = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentSubTotal || 0),
          0,
        );
        totalAgentHST = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentHST || 0),
          0,
        );
        totalAgentTotal = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentTotal || 0),
          0,
        );
      }
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        const totalListingToAgents = agentsForNetToOffice.reduce(
          (sum, a) => sum + parseFloat(a.agentListing || 0),
          0,
        );
        const totalSellingToAgents = agentsForNetToOffice.reduce(
          (sum, a) => sum + parseFloat(a.agentSelling || 0),
          0,
        );

        netToOfficeListing = (
          parseFloat(baseOfficeCommissionListing) -
          totalListingToAgents -
          buyerRebateCols.listing
        ).toFixed(2);

        netToOfficeSelling = (
          parseFloat(baseOfficeCommissionSelling) -
          totalSellingToAgents -
          buyerRebateCols.selling
        ).toFixed(2);

        netToOfficeSubTotal = (
          parseFloat(netToOfficeListing) + parseFloat(netToOfficeSelling)
        ).toFixed(2);
        netToOfficeHST = (parseFloat(netToOfficeSubTotal) * taxRate).toFixed(2);
        netToOfficeTotal = (
          parseFloat(netToOfficeSubTotal) + parseFloat(netToOfficeHST)
        ).toFixed(2);
      } else if (!isCoOperatingSide && agentsForNetToOffice.length === 2) {
        // Listing-side two-agent plan: sub-total = plan fees + office expenses;
        // HST = tax on plan fees + override HST to office + office expense tax.
        const totalPlanFees = agentsForNetToOffice.reduce(
          (total, agent) =>
            total +
            parseFloat(agent.feesDeducted || 0) +
            officeExpensesBaseForAgent(agent, taxRate),
          0,
        );
        const planFeesHST = agentsForNetToOffice.reduce(
          (total, agent) =>
            total +
            roundTo2((parseFloat(agent.feesDeducted || 0) || 0) * taxRate),
          0,
        );
        const overriddenHSTToOffice = agentsForNetToOffice.reduce(
          (total, agent) => {
            const isTaxOverridden =
              agent.taxOverridden === true || agent.taxOverridden === "true";
            return (
              total +
              (isTaxOverridden ? parseFloat(agent.amount || 0) * taxRate : 0) +
              officeExpensesTaxForAgent(agent, taxRate)
            );
          },
          0,
        );
        netToOfficeSubTotal = totalPlanFees.toFixed(2);
        netToOfficeHST = (planFeesHST + overriddenHSTToOffice).toFixed(2);
        netToOfficeTotal = (
          totalPlanFees +
          planFeesHST +
          overriddenHSTToOffice
        ).toFixed(2);
        // For listing side with 2 agents, distribute the amounts appropriately
        // Since this is listing side, put the amounts in listing column
        netToOfficeListing = netToOfficeSubTotal;
        netToOfficeSelling = "0.00";
      } else {
        netToOfficeListing = (
          parseFloat(baseOfficeCommissionListing) -
          totalAgentListing -
          buyerRebateCols.listing
        ).toFixed(2);
        netToOfficeSelling = (
          parseFloat(baseOfficeCommissionSelling) -
          totalAgentSelling -
          buyerRebateCols.selling
        ).toFixed(2);
        netToOfficeSubTotal = (
          parseFloat(netToOfficeListing) + parseFloat(netToOfficeSelling)
        ).toFixed(2);
        netToOfficeHST = (parseFloat(netToOfficeSubTotal) * taxRate).toFixed(2);
        netToOfficeTotal = (
          parseFloat(netToOfficeSubTotal) + parseFloat(netToOfficeHST)
        ).toFixed(2);
      }
      const isTwoAgentPlan =
        !isCoOperatingSide && agentsForNetToOffice.length === 2;
      if (!isTwoAgentPlan) {
        ({
          netToOfficeListing,
          netToOfficeSelling,
          netToOfficeSubTotal,
          netToOfficeHST,
          netToOfficeTotal,
        } = addOfficeExpensesToNetToOfficeValues(
          {
            netToOfficeListing: netToOfficeListing || "0.00",
            netToOfficeSelling: netToOfficeSelling || "0.00",
            netToOfficeSubTotal,
            netToOfficeHST,
            netToOfficeTotal,
          },
          agentsForNetToOffice,
          tradeClassification,
          taxRate,
        ));
      }
      // Format the outside brokers data (include referral agents from Agent Info for trade record sheet print)
      const getEndValueForPrintBroker = (b) => {
        const matchingBroker1 = trade.outsideBrokers?.find((ob) => {
          const outsideBrokerName = `${ob.firstName} ${ob.lastName}`.trim();
          return (
            outsideBrokerName === b.agentName || ob.company === b.brokerage
          );
        });
        if (matchingBroker1) {
          if (
            matchingBroker1.end?.toLowerCase().includes("listing") ||
            matchingBroker1.type?.toLowerCase().includes("listing")
          )
            return "Listing";
        }
        if (
          b.end?.toLowerCase().includes("listing") ||
          b.type?.toLowerCase().includes("listing")
        )
          return "Listing";
        return "Selling";
      };
      const formattedOutsideBrokersBase =
        trade.commission?.outsideBrokersRows?.map((broker) => ({
          agentName: broker.agentName || "-",
          brokerage: broker.brokerage || "-",
          end: getEndValueForPrintBroker(broker),
          percentage: broker.percentage,
          sellingAmount: broker.sellingAmount || "0.00",
          tax: broker.tax || "0.00",
          total: broker.total || "0.00",
        })) || [];
      const referralAgentsForPrint = (trade.agentCommissionList || []).filter(
        (a) => (a.classification || "").toUpperCase() === "REFERRAL",
      );
      const referralCommissionRowsForPrint = referralAgentsForPrint.map(
        (a) => ({
          agentName: a.agentName || "-",
          brokerage: a.agentName || "Referral",
          end: "Selling",
          percentage: a.percentage,
          sellingAmount: a.amount || "0.00",
          tax: a.tax || "0.00",
          total: a.total || "0.00",
        }),
      );
      const formattedOutsideBrokers = [
        ...formattedOutsideBrokersBase,
        ...referralCommissionRowsForPrint,
      ];
      const referralAsBrokersForPrint = referralAgentsForPrint.map((a) => ({
        type: "Referral",
        firstName: "-",
        lastName: "-",
        company: a.agentName || "Referral",
        end: "Selling End",
        primaryPhone: "-",
      }));
      const allOutsideBrokersForPrint = [
        ...(trade.outsideBrokers || []),
        ...referralAsBrokersForPrint,
      ];
      // Agent Details in print excludes referral agents (they appear under Outside Brokers)
      const agentDetailsForPrint = (agentCalculationsFiltered || []).filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      // ADE has LISTING + SELLING rows per agent — signatures need each person once
      const agentsForTrustAgreement = (() => {
        const byKey = new Map();
        for (const agent of agentDetailsForPrint) {
          const id =
            agent.agentId != null && String(agent.agentId).trim() !== ""
              ? String(agent.agentId).trim()
              : "";
          const name = String(agent.agentName || "")
            .trim()
            .toLowerCase();
          const key = id || name;
          if (!key || byKey.has(key)) continue;
          byKey.set(key, agent);
        }
        return [...byKey.values()];
      })();
      // Get address information from keyInfo
      const streetNumber = get(trade, "keyInfo.streetNumber") || "";
      const streetName = get(trade, "keyInfo.streetName") || "";
      const unit = get(trade, "keyInfo.unit") || "";
      const city = get(trade, "keyInfo.city") || "";
      const province = get(trade, "keyInfo.province") || "";
      const postalCode = get(trade, "keyInfo.postalCode") || "";
      // Format address for display
      const addressLine1 =
        streetNumber && streetName ? `${streetNumber} ${streetName}` : "";
      const addressLine2 = unit || "";
      const addressLine3 = city && province ? `${city}, ${province}` : "";
      const addressLine4 = postalCode || "";
      const printContent = `


     <!DOCTYPE html>
<html>
<head>
<title>Trade Record Sheet - ${trade.tradeNumber}</title>
<style>
/* A4 Optimized Styling - 210mm x 297mm */
* { box-sizing: border-box; margin: 0; padding: 0; }

body {
font-family: Arial, sans-serif;
margin: 0;
padding: 15mm 12mm; /* A4 margins: top/bottom 15mm, left/right 12mm */
font-size: 10px;
line-height: 1.4;
width: 210mm; /* A4 width */
background: white;
}

.header {
display: flex;
justify-content: space-between;
align-items: flex-start;
margin-bottom: 15px;
width: 100%;
}

.logo { width: 70px; height: auto; }

.title {
font-size: 16px;
font-weight: bold;
text-align: center;
flex-grow: 1;
padding: 0 10px;
}

.date-address {
text-align: right;
font-size: 9px;
line-height: 1.3;
}

.date { font-weight: bold; margin-bottom: 4px; }

.address { line-height: 1.3; }

.section {
margin-bottom: 12px;
page-break-inside: avoid; /* Prevent sections from breaking across pages */
}

.section.section-allow-break {
page-break-inside: auto; /* Allow Financial and following to flow into gap and next page */
}

.section-title {
font-size: 12px;
font-weight: bold;
margin-bottom: 6px;
border-bottom: 1px solid #333;
padding-bottom: 2px;
}

.property-grid {
display: grid;
grid-template-columns: 1fr 1fr;
gap: 6px;
margin-bottom: 8px;
}

.property-item {
display: flex;
justify-content: space-between;
font-size: 9px;
line-height: 1.3;
}

.property-label { font-weight: bold; margin-right: 5px; }

table {
width: 100%;
border-collapse: collapse;
margin-bottom: 8px;
font-size: 8px;
page-break-inside: auto;
}

tr { page-break-inside: avoid; page-break-after: auto; }

th, td {
border: 1px solid #ccc;
padding: 3px 4px;
text-align: left;
line-height: 1.2;
}

th {
background-color: #f5f5f5;
font-weight: bold;
font-size: 8px;
}

.financial-grid {
display: grid;
grid-template-columns: 1fr 1fr 1fr;
gap: 8px;
margin-bottom: 10px;
}

.financial-item { text-align: center; }

.financial-label {
font-weight: bold;
margin-bottom: 3px;
font-size: 9px;
}

.financial-value { font-size: 12px; font-weight: bold; }

/* Page break helpers */
.page-break { page-break-after: always; }

.no-break { page-break-inside: avoid; }

@media print {
body {
margin: 0;
padding: 15mm 12mm;
}

.no-print { display: none !important; }

button { display: none !important; }
}
</style>
</head>

<body>

<div class="header">

<div style="display: flex; flex-direction: column; align-items: flex-start;">
<img src="/logo3.jpg" alt="Logo" class="logo">

<div style="font-size: 9px; line-height: 1.2; margin-top: 5px;">
ABC Realty Inc., Brokerage<br>
634 Champlain St Dieppe <br>
NB E1A 1P4<br>
Phone: 123 456 7890
</div>

</div>

<div class="title">Trade Record Sheet</div>

<div class="date-address">

<div class="date">${currentDate}</div>

<div class="address">
${addressLine1}<br>
${addressLine2}<br>
${addressLine3}<br>
${addressLine4}
</div>

<div style="font-size: 10px; font-weight: bold; margin-top: 5px;">
Trade Number - ${get(trade, "keyInfo.tradeNumber") || trade.tradeNumber || "-"}
</div>

</div>

</div>

<div class="section">

<div class="section-title">Property</div>

<div class="property-grid">

<div class="property-item">
<span class="property-label">Type:</span>
<span>${get(trade, "keyInfo.propertyType")}</span>



          </div>

<div class="property-item">
  <span class="property-label">Classification:</span>
  <span>${get(trade, "keyInfo.classification")}</span>
</div>
<div class="property-item">
  <span class="property-label">Status:</span>
  <span>${get(trade, "keyInfo.status")}</span>
</div>

<div class="property-item">
  <span class="property-label">MLS Number:</span>
  <span>${get(trade, "keyInfo.mlsNumber")}</span>
</div>

<div class="property-item">
  <span class="property-label">Offer Date:</span>
  <span>${get(trade, "keyInfo.offerDate") ? formatDate(get(trade, "keyInfo.offerDate")) : ""}</span>
</div>

<div class="property-item">
  <span class="property-label">Entry Date:</span>
  <span>${get(trade, "keyInfo.entryDate") ? formatDate(get(trade, "keyInfo.entryDate")) : ""}</span>
</div>

<div class="property-item">
  <span class="property-label">Firm Date:</span>
  <span>${get(trade, "keyInfo.firmDate") ? formatDate(get(trade, "keyInfo.firmDate")) : ""}</span>
</div>

<div class="property-item">
  <span class="property-label">Close Date:</span>
  <span>${get(trade, "keyInfo.closeDate") ? formatDate(get(trade, "keyInfo.closeDate")) : ""}</span>
</div>

</div>

</div>

<div class="section">

  <div class="section-title">Conditions</div>

  ${
    trade.conditions && trade.conditions.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Condition</th>
        <th>Due Date</th>
        <th>Condition Met Date</th>
      </tr>
    </thead>

    <tbody>
      ${trade.conditions
        .map(
          (condition) => `

        <tr>
          <td>${condition.conditionText || "-"}</td>
          <td>${
            condition.dueDate ? formatDate(condition.dueDate) : "-"
          }</td>
          <td>${
            condition.conditionMetDate
              ? formatDate(condition.conditionMetDate)
              : "Not met"
          }</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No conditions available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Contacts</div>

  ${
    trade.people && trade.people.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Type</th>
        <th>First Name</th>
        <th>Last Name</th>
        <th>Primary Phone</th>
        <th>Secondary Phone</th>
        <th>Email</th>
        <th>Company Name</th>
      </tr>
    </thead>

    <tbody>
      ${trade.people
        .map(
          (person) => `

        <tr>
          <td>${person.type || "-"}</td>
          <td>${person.firstName || "-"}</td>
          <td>${person.lastName || "-"}</td>
          <td>${person.primaryPhone || "-"}</td>
          <td>${person.secondaryPhone || "-"}</td>
          <td>${person.email || "-"}</td>
          <td>${person.companyName || "-"}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No contacts available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Outside Brokers</div>

  ${
    allOutsideBrokersForPrint && allOutsideBrokersForPrint.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Type</th>
        <th>End</th>
        <th>First Name</th>
        <th>Last Name</th>
        <th>Company</th>
        <th>Phone Number</th>
      </tr>
    </thead>

    <tbody>
      ${allOutsideBrokersForPrint
        .map(
          (broker) => `

        <tr>
          <td>${broker.type || "-"}</td>
          <td>${broker.end || "-"}</td>
          <td>${broker.firstName || "-"}</td>
          <td>${broker.lastName || "-"}</td>
          <td>${broker.company || "-"}</td>
          <td>${broker.primaryPhone || "-"}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No outside brokers available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Outside Brokers Commission Details</div>

  ${
    formattedOutsideBrokers.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Agent Name</th>
        <th>Brokerage</th>
        <th>End</th>
        <th>Percentage</th>
        <th>Selling Amount</th>
        <th>Tax</th>
        <th>Total</th>
      </tr>
    </thead>

    <tbody>
      ${formattedOutsideBrokers
        .map(
          (broker) => `

        <tr>
          <td>${broker.agentName}</td>
          <td>${broker.brokerage}</td>
          <td>${broker.end}</td>
          <td>${formatPercentDisplayForSheet(broker.percentage)}</td>
          <td>${formatCurrency(broker.sellingAmount)}</td>
          <td>${formatCurrency(broker.tax)}</td>
          <td>${formatCurrency(broker.total)}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No outside brokers commission details available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Deposit</div>

  ${
    trade.trustRecords && trade.trustRecords.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>We Hold</th>
        <th>Received</th>
        <th>Received From</th>
        <th>Held By</th>
        <th>Deposit Date</th>
        <th>Reference</th>
        <th>Amount</th>
        <th>Payment Type</th>
      </tr>
    </thead>

    <tbody>
      ${trade.trustRecords
        .map(
          (record) => `

        <tr>
          <td>${record.weHold || "-"}</td>
          <td>${record.received || "-"}</td>
          <td>${record.receivedFrom || "-"}</td>
          <td>${record.heldBy || "-"}</td>
          <td>${
            record.depositDate || record.date
              ? formatDate(record.depositDate || record.date)
              : "-"
          }</td>
          <td>${record.reference || "-"}</td>
          <td>${formatCurrency(record.amount || 0)}</td>
          <td>${record.paymentType || "-"}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No deposit records available.</p>"
  }

</div>

<div class="section section-allow-break">

  <div class="section-title">Financial</div>

  <div style="font-weight:bold; font-size:12px; margin-bottom:4px;">Financial:</div>

  <div style="margin-bottom:4px; font-size:10px;">
    <div>Selling Price: <span style="font-weight:bold;">${formatCurrency(
      commissionIncome.sellPrice || trade.keyInfo?.sellPrice || 0,
    )}</span></div>
  </div>

  <div style="margin-bottom:4px; font-size:10px;">
    <div>Listing Commission: <span style="font-weight:bold;">${parseFloat(
      trade.keyInfo?.listCommission || 0,
    )}%</span></div>
  </div>

  <div style="margin-bottom:4px; font-size:10px;">
    <div>Selling Commission: <span style="font-weight:bold;">${parseFloat(
      trade.keyInfo?.sellCommission || 0,
    )}%</span></div>
  </div>

  <div style="display:flex; font-weight:bold; border-bottom:1px solid #ccc; padding:3px 0; border-top:1px solid #ccc; font-size:9px;">
    <div style="width:25%"></div>
    <div style="width:12%; text-align:center;">Listing</div>
    <div style="width:12%; text-align:center;">Selling</div>
    <div style="width:12%; text-align:center;">Sub-Total</div>
    <div style="width:12%; text-align:center;">HST</div>
    <div style="width:12%; text-align:center;">Total</div>
  </div>

  <div style="font-weight:bold; margin-top:6px; margin-bottom:0; font-size:10px;">Income</div>

  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">Commission</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      listingAmount,
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      sellingAmount,
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(subTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(hst)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(total)}</div>
  </div>

  ${
    isCoOperatingSide && referralForExpenses.length === 0
      ? showBaseOfficeAndNetToOffice
        ? `
  <div style="display:flex; padding:3px 0; font-size:9px;">
    <div style="width:25%">Base Office Commission</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionListing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSelling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSubTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionHST)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionTotal)}</div>
  </div>
  `
        : ""
      : isCoOperatingSide && referralForExpenses.length > 0
        ? showBaseOfficeAndNetToOffice
          ? `
  <div style="font-weight:bold; margin-top:6px; font-size:10px;">Expenses</div>
  ${expensesRowsForPrint
    .filter((row) => (row.name || "").includes("Referral"))
    .map(
      (row) => `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">Ref. ${(row.name || "").replace(/\s*-\s*Referral\s*$/i, "")}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.listing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.selling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.subTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.tax)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.total)}</div>
  </div>
  `,
    )
    .join("")}
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">Base Office Commission</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionListing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSelling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSubTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionHST)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionTotal)}</div>
  </div>
  `
          : ""
        : `

  <div style="font-weight:bold; margin-top:6px; font-size:10px;">Expenses</div>
  ${expensesRowsForPrint
    .map(
      (row) => `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">${row.name}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.listing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.selling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.subTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.tax)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.total)}</div>
  </div>
  `,
    )
    .join("")}
  ${
    showBaseOfficeAndNetToOffice
      ? `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">Base Office Commission</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionListing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSelling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSubTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionHST)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionTotal)}</div>
  </div>
  `
      : ""
  }
  `
  }

  <div style="font-weight:bold; margin-top:6px; font-size:10px;">Agents</div>

  ${agentDetailsForPrint
    .map(
      (agent) => `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">(${agent.agentId || ""}) ${
      agent.agentName || ""
    }</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentListing || "0.00",
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentSelling || "0.00",
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentSubTotal || "0.00",
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentHST || "0.00",
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentTotal || "0.00",
    )}</div>
  </div>
  ${(agent.officeExpenseFinancialRows || [])
    .map(
      (row) => `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%; padding-left:12px;">${row.label}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.listing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.selling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.subTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.hst)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.total)}</div>
  </div>`,
    )
    .join("")}
  `,
    )
    .join("")}

  <!-- Liabilities Section -->

  ${
    totalBuyerRebate > 0
      ? `

  <div style="font-weight:bold; margin-top:6px; font-size:10px;">Liabilities</div>

  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">

    <div style="width:25%">Buyer Rebate</div>

    <div style="width:12%; text-align:center;">${formatCurrency(
      buyerRebateListing,
    )}</div>

    <div style="width:12%; text-align:center;">${formatCurrency(
      buyerRebateSelling,
    )}</div>

<div style="width:12%; text-align:center;">${formatCurrency(
          (
            parseFloat(buyerRebateListing) + parseFloat(buyerRebateSelling)
          ).toFixed(2),
        )}</div>

<div style="width:12%; text-align:center;">${formatCurrency("0.00")}</div>

<div style="width:12%; text-align:center;">${formatCurrency(
          (
            parseFloat(buyerRebateListing) + parseFloat(buyerRebateSelling)
          ).toFixed(2),
        )}</div>

</div>

`
      : ""
  }

${
  showBaseOfficeAndNetToOffice
    ? `
<div style="font-weight:bold; margin-top:6px; font-size:10px;">Net to Office</div>
<div style="display:flex; border-bottom:3px double #000; padding:3px 0; font-size:9px;">
  <div style="width:25%"></div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeListing)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeSelling)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeSubTotal)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeHST)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeTotal)}</div>
</div>
</div>
`
    : `
</div>
`
}

${
  tradeClassification !== "AGENT DOUBLE ENDER"
    ? `

<div class="section section-allow-break">

  <div class="section-title">Agent Details</div>

  ${
    agentDetailsForPrint && agentDetailsForPrint.length > 0
      ? `

  <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">

    <thead>
      <tr style="background-color:#f2f2f2;">
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Name</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Number</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Percentage</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Lead</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Payment Plan</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Base</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Plan Amount</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Office Expenses</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Buyer Rebate</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Total</th>
      </tr>
    </thead>

    <tbody>
      ${agentDetailsForPrint
        .map(
          (agent) => `

        <tr>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentName || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentId || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatPercentDisplayForSheet(
            agent.percentage,
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.lead != null && agent.lead !== "" ? agent.lead : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            getPaymentPlanDisplayName(agent.feeInfo) || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            parseFloat(agent.amount || 0),
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.feesDeducted || 0,
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE) > 0
              ? formatCurrency(
                  officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE),
                )
              : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.buyerRebateIncluded === "yes" && agent.buyerRebateAmount
              ? formatCurrency(agent.buyerRebateAmount)
              : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.netCommission != null &&
              String(agent.netCommission).trim() !== ""
              ? parseFloat(agent.netCommission)
              : parseFloat(agent.amount || 0) +
                  parseFloat(agent.tax || 0) -
                  parseFloat(
                    agent.totalFees != null && agent.totalFees !== ""
                      ? agent.totalFees
                      : agent.feesDeducted || 0,
                  ) -
                  (agent.buyerRebateIncluded === "yes" &&
                  agent.buyerRebateAmount
                    ? parseFloat(agent.buyerRebateAmount || 0)
                    : 0),
          )}</td>

        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No agent payment details available.</p>"
  }

</div>

`
    : `

<div class="section section-allow-break">

  <div class="section-title">Agent Details</div>

  ${
    agentDetailsForPrint && agentDetailsForPrint.length > 0
      ? `

  <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">

    <thead>
      <tr style="background-color:#f2f2f2;">
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Name</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Number</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Percentage</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Lead</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Payment Plan</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Base</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Plan Amount</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Office Expenses</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Buyer Rebate</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Total</th>
      </tr>
    </thead>

    <tbody>
      ${agentDetailsForPrint
        .filter((agent) => agent.classification === "AGENT DOUBLE ENDER")
        .map(
          (agent) => `

        <tr>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentName || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentId || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatPercentDisplayForSheet(
            agent.percentage,
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.lead != null && agent.lead !== "" ? agent.lead : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            getPaymentPlanDisplayName(agent.feeInfo) || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            parseFloat(agent.amount || 0),
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.feesDeducted || 0,
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE) > 0
              ? formatCurrency(
                  officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE),
                )
              : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.buyerRebateIncluded === "yes" && agent.buyerRebateAmount
              ? formatCurrency(agent.buyerRebateAmount)
              : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.netCommission != null &&
              String(agent.netCommission).trim() !== ""
              ? parseFloat(agent.netCommission)
              : parseFloat(agent.amount || 0) +
                  parseFloat(agent.tax || 0) -
                  parseFloat(
                    agent.totalFees != null && agent.totalFees !== ""
                      ? agent.totalFees
                      : agent.feesDeducted || 0,
                  ) -
                  (agent.buyerRebateIncluded === "yes" &&
                  agent.buyerRebateAmount
                    ? parseFloat(agent.buyerRebateAmount || 0)
                    : 0),
          )}</td>

        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No agent payment details available.</p>"
  }

</div>

`
}

<div class="section section-allow-break">

  <div class="section-title">Trust Activity</div>

  ${
    trade.trustRecords && trade.trustRecords.length > 0
      ? `<table><thead><tr>
          <th>Received From</th><th>Deposit Date</th><th>Reference</th><th>Amount</th>
        </tr></thead><tbody>
        ${trade.trustRecords
          .map(
            (record) => `<tr>
              <td>${record.receivedFrom || "-"}</td>
              <td>${
            record.depositDate || record.date
              ? formatDate(record.depositDate || record.date)
              : "-"
          }</td>
              <td>${record.reference || "-"}</td>
              <td>${formatCurrency(record.amount || 0)}</td>
            </tr>`,
          )
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No trust records available.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Real Estate Trust Payments</div>

  ${
    realEstateTrustEFTs.length > 0
      ? `<table><thead><tr>
          <th>EFT #</th><th>Date</th><th>Amount</th><th>Recipient</th><th>Type</th><th>Description</th>
        </tr></thead><tbody>
        ${realEstateTrustEFTs
          .map(
            (eft) => `<tr>
              <td>${eft.eftNumber || ""}</td>
              <td>${
                eft.chequeDate || eft.date
                  ? formatDate(eft.chequeDate || eft.date)
                  : "-"
              }</td>
              <td>${formatCurrency(eft.amount || "")}</td>
              <td>${eft.recipient || ""}</td>
              <td>${getEFTTypeDisplayName(eft.type) || ""}</td>
              <td>${eft.description || ""}</td>
            </tr>`,
          )
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No Real Estate Trust Payments found for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Commission Trust Activity</div>

          ${(() => {
            // For Listing Side, include CommissionTransfer from Real Estate Trust Payments as first transaction

            // For Co-Operating Side, include CommissionTransfer with "Commission Received" description as first transaction

            const commissionTrustEFTsForActivity =
              filterCommissionTrustActivityEFTs(commissionTrustEFTs);

            let displayCommissionTrustEFTs = [
              ...commissionTrustEFTsForActivity,
            ];

            // Normalize classification for comparison (case-insensitive)

            const normalizedClassification = (tradeClassification || "")

              .toUpperCase()

              .trim();

            if (
              normalizedClassification === "LISTING SIDE" ||
              normalizedClassification === "AGENT DOUBLE ENDER"
            ) {
              const commissionTransferEFT = realEstateTrustEFTs.find(
                (eft) => eft.type === "CommissionTransfer",
              );

              console.log(
                "Commission Transfer EFT found:",

                commissionTransferEFT,

                "for classification:",

                normalizedClassification,

                "realEstateTrustEFTs:",

                realEstateTrustEFTs,
              );

              if (commissionTransferEFT) {
                displayCommissionTrustEFTs = [
                  commissionTransferEFT,

                  ...commissionTrustEFTsForActivity,
                ];
              }

              // Check if trust amount is less than total commission and add "Received From" transaction as second item

              const trustRecords = trade.trustRecords || [];

              const commissionIncome =
                trade.commission?.commissionIncomeRows?.[0] || {};

              const listingAmount = commissionIncome.listingAmount
                ? parseFloat(commissionIncome.listingAmount)
                : 0;

              const sellingAmount = commissionIncome.sellingAmount
                ? parseFloat(commissionIncome.sellingAmount)
                : 0;

              const taxRateForTrust =
                trade.customTaxRate != null
                  ? trade.customTaxRate
                  : DEFAULT_TAX_RATE;
              const listingTax = listingAmount * taxRateForTrust;

              const sellingTax = sellingAmount * taxRateForTrust;

              const totalCommission =
                listingAmount + sellingAmount + listingTax + sellingTax;

              // Calculate total trust amount

              const trustAmount = trustRecords.reduce((sum, record) => {
                const amount = parseFloat(
                  record.amount || record.depositAmount || record.total || 0,
                );

                return sum + amount;
              }, 0);

              // If trust amount is less than total commission, find and add "Received From" transaction

              if (trustAmount < totalCommission) {
                // First, try to find the "Received From" transaction from ledger entries (account 10004 with "Received From")

                // Exclude the CommissionTransfer entry by checking if it matches the CommissionTransfer EFT number

                let receivedFromLedger = null;

                if (ledgerEntries && ledgerEntries.length > 0) {
                  // Get the CommissionTransfer EFT number to exclude it

                  const commissionTransferEFTNumber =
                    commissionTransferEFT?.eftNumber?.toString() || "";

                  // Find ledger entries that have "Received From:" but exclude the CommissionTransfer one

                  // The balance payment entry has format: "Trade #: X - Received From: Name" or "Trade #: X - Address - Received From: Name" (no "Commission Trust")

                  // The CommissionTransfer entry has format: "Trade #: X, Received From: Name - Commission Trust - Address"

                  // Also check for entries from the "final 2 transactions" section (transaction 13) which uses popupDescription

                  const candidateEntries = ledgerEntries.filter(
                    (entry) =>
                      entry.accountNumber === "10004" &&
                      entry.debit > 0 &&
                      entry.description &&
                      (entry.description.includes("Received From:") ||
                        entry.description.includes("Received from:")) &&
                      // Exclude if it matches the CommissionTransfer EFT number

                      entry.eftNumber?.toString() !==
                        commissionTransferEFTNumber &&
                      // Exclude entries that have "Commission Trust" in description (those are from CommissionTransfer)

                      !entry.description.includes("Commission Trust") &&
                      // Exclude entries that have "Commission Trust" anywhere (to be safe)

                      !entry.description

                        .toLowerCase()

                        .includes("commission trust") &&
                      !isReversalLedgerEftRef(entry.eftNumber),
                  );

                  // If we have candidates, prefer the one that matches the pattern from final transactions

                  // (format: "Trade #: X - Received From: Name" or "Trade #: X - Address - Received From: Name")

                  if (candidateEntries.length > 0) {
                    // Find the one that matches the final transaction pattern (has " - Received From:" with dash before it)

                    receivedFromLedger =
                      candidateEntries.find((entry) =>
                        entry.description.includes(" - Received From:"),
                      ) || candidateEntries[0];
                  }
                }

                if (receivedFromLedger) {
                  // Extract recipient from description (format: "Trade #: X - Received From: Recipient - Commission Trust - Address")

                  let recipient = receivedFromLedger.payee || "";

                  if (!recipient && receivedFromLedger.description) {
                    const receivedFromMatch =
                      receivedFromLedger.description.match(
                        /Received From:\s*([^-]+?)(?:\s*-|$)/i,
                      );

                    if (receivedFromMatch) {
                      recipient = receivedFromMatch[1].trim();
                    }
                  }

                  // Create the balance commission payment transaction from ledger entry

                  const balanceCommissionPayment = {
                    eftNumber: resolveCommissionTrustDisplayEftNumber(
                      receivedFromLedger,
                      {
                        eftReversals,
                        realEstateTrustEFTs,
                        commissionTrustEFTs,
                        ledgerEntries,
                      },
                    ),

                    date:
                      receivedFromLedger.date || receivedFromLedger.chequeDate,

                    chequeDate:
                      receivedFromLedger.chequeDate || receivedFromLedger.date,

                    amount:
                      receivedFromLedger.debit || receivedFromLedger.amount,

                    recipient: recipient || "N/A",

                    type: "BalanceCommissionPayment",

                    description: "Balance Amount Received",

                    _id: `balance-${receivedFromLedger._id || Date.now()}`,
                  };

                  // Insert as second transaction (after CommissionTransfer)

                  if (commissionTransferEFT) {
                    displayCommissionTrustEFTs = [
                      commissionTransferEFT,

                      balanceCommissionPayment,

                      ...commissionTrustEFTsForActivity,
                    ];
                  } else {
                    displayCommissionTrustEFTs = [
                      balanceCommissionPayment,

                      ...commissionTrustEFTsForActivity,
                    ];
                  }
                } else {
                  // Fallback: Find the "Received From" transaction from trust records

                  const receivedFromRecord = trustRecords.find(
                    (record) =>
                      record.receivedFrom && record.paymentType === "EFT",
                  );

                  if (receivedFromRecord) {
                    // Extract EFT number from reference or use a default

                    const eftNumber =
                      receivedFromRecord.reference?.replace("EFT#", "") ||
                      receivedFromRecord.eftNumber ||
                      "";

                    // Create the balance commission payment transaction

                    const balanceCommissionPayment = {
                      eftNumber: eftNumber,

                      date:
                        receivedFromRecord.depositDate ||
                        receivedFromRecord.date,

                      chequeDate:
                        receivedFromRecord.depositDate ||
                        receivedFromRecord.date,

                      amount: parseFloat(
                        receivedFromRecord.amount ||
                          receivedFromRecord.depositAmount ||
                          receivedFromRecord.total ||
                          0,
                      ),

                      recipient: receivedFromRecord.receivedFrom || "",

                      type: "BalanceCommissionPayment",

                      description: "Balance Amount Received",

                      _id: `balance-${receivedFromRecord._id || Date.now()}`,
                    };

                    // Insert as second transaction (after CommissionTransfer)

                    if (commissionTransferEFT) {
                      displayCommissionTrustEFTs = [
                        commissionTransferEFT,

                        balanceCommissionPayment,

                        ...commissionTrustEFTsForActivity,
                      ];
                    } else {
                      displayCommissionTrustEFTs = [
                        balanceCommissionPayment,

                        ...commissionTrustEFTsForActivity,
                      ];
                    }
                  }
                }
              }
            } else if (
              tradeClassification === "CO-OPERATING SIDE" ||
              tradeClassification === "Co-Operating Side"
            ) {
              // First, try to find CommissionTransfer in Real Estate Trust Payments

              let commissionReceivedEFT = realEstateTrustEFTs.find(
                (eft) => eft.type === "CommissionTransfer",
              );

              // If not found, look for commission trust ledger phrasing (legacy Received From / Paid To on 10004 credits)

              if (!commissionReceivedEFT) {
                commissionReceivedEFT = commissionTrustEFTsForActivity.find(
                  (eft) =>
                    eft.description &&
                    (/(?:Received From|Received from|Paid To|Paid to):\s*[\s\S]*Commission Trust/i.test(
                      eft.description,
                    ) ||
                      eft.description.includes("Commission Received")),
                );
              }

              // If still not found, check ledger entries from trade finalize (account 10004 with "Received From")

              if (
                !commissionReceivedEFT &&
                ledgerEntries &&
                ledgerEntries.length > 0
              ) {
                const commissionReceivedLedger = ledgerEntries.find(
                  (entry) =>
                    entry.accountNumber === "10004" &&
                    entry.debit > 0 &&
                    entry.description &&
                    (entry.description.includes("Received From") ||
                      entry.description.includes("Received from")) &&
                    !isReversalLedgerEftRef(entry.eftNumber),
                );

                if (commissionReceivedLedger) {
                  // Extract recipient from description (format: "Trade #: X - Address - Received From: Recipient")

                  let recipient = commissionReceivedLedger.payee || "";

                  if (!recipient && commissionReceivedLedger.description) {
                    const receivedFromMatch =
                      commissionReceivedLedger.description.match(
                        /Received From:\s*([^-]+?)(?:\s*-|$)/i,
                      );

                    if (receivedFromMatch) {
                      recipient = receivedFromMatch[1].trim();
                    }
                  }

                  // Create EFT-like object from ledger entry

                  commissionReceivedEFT = {
                    eftNumber: resolveCommissionTrustDisplayEftNumber(
                      commissionReceivedLedger,
                      {
                        eftReversals,
                        realEstateTrustEFTs,
                        commissionTrustEFTs,
                        ledgerEntries,
                      },
                    ),

                    date:
                      commissionReceivedLedger.date ||
                      commissionReceivedLedger.chequeDate,

                    chequeDate:
                      commissionReceivedLedger.chequeDate ||
                      commissionReceivedLedger.date,

                    amount:
                      commissionReceivedLedger.debit ||
                      commissionReceivedLedger.amount,

                    recipient: recipient || "N/A",

                    type: "CommissionTransfer",

                    description: "Commission Received",

                    _id: commissionReceivedLedger._id,
                  };
                }
              }

              // If still not found, check by recipient name (common outside broker names)

              if (!commissionReceivedEFT) {
                commissionReceivedEFT = commissionTrustEFTsForActivity.find(
                  (eft) =>
                    eft.recipient &&
                    (eft.recipient.includes("ABC") ||
                      eft.recipient.includes("Realty Inc")),
                );
              }

              if (commissionReceivedEFT) {
                // Create a formatted transaction with "Commission Received" as description

                const formattedEFT = {
                  ...commissionReceivedEFT,

                  description: "Commission Received",
                };

                // If the transaction was found in commissionTrustEFTs, remove it from the array first

                const remainingEFTs =
                  commissionReceivedEFT._id &&
                  commissionTrustEFTsForActivity.some(
                    (eft) => eft._id === commissionReceivedEFT._id,
                  )
                    ? commissionTrustEFTsForActivity.filter(
                        (eft) => eft._id !== commissionReceivedEFT._id,
                      )
                    : commissionTrustEFTsForActivity;

                displayCommissionTrustEFTs = [formattedEFT, ...remainingEFTs];
              }
            }

            if (
              !showBaseOfficeAndNetToOffice &&
              agentCalculationsFiltered &&
              agentCalculationsFiltered.length > 0
            ) {
              const selectedAgents = agentCalculationsFiltered;
              displayCommissionTrustEFTs = displayCommissionTrustEFTs.filter(
                (eft) => {
                  if (eft.type !== "AgentCommissionTransfer") return false;
                  return selectedAgents.some((selectedAgent) => {
                    const selectedAgentId =
                      selectedAgent?.agentId != null
                        ? selectedAgent.agentId.toString()
                        : "";
                    const selectedAgentName = (selectedAgent?.agentName || "")
                      .trim()
                      .toLowerCase();

                    const matchId =
                      selectedAgentId &&
                      eft.agentId != null &&
                      eft.agentId.toString() === selectedAgentId;
                    const matchName =
                      selectedAgentName &&
                      eft.agentName &&
                      (eft.agentName || "").trim().toLowerCase() ===
                        selectedAgentName;
                    const matchRecipient =
                      selectedAgentName &&
                      eft.recipient &&
                      (eft.recipient || "")
                        .trim()
                        .toLowerCase()
                        .includes(selectedAgentName);
                    return matchId || matchName || matchRecipient;
                  });
                },
              );
            }

            displayCommissionTrustEFTs = filterCommissionTrustActivityEFTs(
              displayCommissionTrustEFTs,
            );

            return displayCommissionTrustEFTs.length > 0
              ? `
              <table><thead><tr>
<th>EFT #</th><th>Date</th><th>Amount</th><th>Recipient</th><th>Type</th><th>Description</th>
</tr></thead><tbody>
${displayCommissionTrustEFTs
  .map(
    (eft) => `<tr>
      <td>${formatCommissionTrustActivityEftNumber(eft, {
        eftReversals,
        realEstateTrustEFTs,
        commissionTrustEFTs,
        ledgerEntries,
      })}</td>
      <td>${
        eft.chequeDate || eft.date
          ? formatDate(eft.chequeDate || eft.date)
          : "-"
      }</td>
      <td>${formatCurrency(eft.amount || "")}</td>
      <td>${eft.recipient || ""}</td>
      <td>${getEFTTypeDisplayName(eft.type) || ""}</td>
      <td>${eft.description || ""}</td>
    </tr>`,
  )
  .join("")}
</tbody></table>`
              : `<div class="text-gray-500">No Commission Trust Activity found for this trade.</div>`;
          })()}

</div>

<div class="section">

  <div class="section-title">EFT Reversals</div>

  ${
    eftReversals && eftReversals.length > 0
      ? `
  <table><thead><tr>
    <th>Reversal Date</th><th>Section</th><th>Original EFT #</th><th>Amount</th><th>Description</th>
  </tr></thead><tbody>
    ${eftReversals
      .map((r) => {
        const sectionLabel =
          r.section === "real-estate"
            ? "Real Estate Trust"
            : r.section === "commission"
              ? "Commission Trust"
              : r.section === "general"
                ? "General Account"
                : r.section === "deposit-received"
                  ? "Commission Received from Builder / Brokerage"
                  : r.section || "";
        const reversalDate = r.reversedAt
          ? formatDate(r.reversedAt)
          : r.chequeDate
            ? formatDate(r.chequeDate)
            : "-";
        const desc = r.description || r.notes || "";
        return `<tr>
          <td>${reversalDate}</td>
          <td>${sectionLabel}</td>
          <td>${r.sourceEftNumber || ""}</td>
          <td>${formatCurrency(r.amount || 0)}</td>
          <td>${desc}</td>
        </tr>`;
      })
      .join("")}
  </tbody></table>
`
      : `<div class="text-gray-500">No EFT reversals recorded for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Commission Recalled</div>

  ${
    commissionRecalls && commissionRecalls.length > 0
      ? `<table><thead><tr>
    <th>Recall Date</th><th>Agent</th><th>Reference</th><th>Amount</th><th>Description</th>
  </tr></thead><tbody>
  ${commissionRecalls
    .map((r) => {
      const recallDate = r.recalledAt ? formatDate(r.recalledAt) : "-";
      const recallSheetDesc = `Trade #: ${r.tradeNumber || ""}, Commission recalled - ${r.agentName || ""}`;
      return `<tr>
        <td>${recallDate}</td>
        <td>${r.agentName || "-"} (${r.agentId || ""})</td>
        <td>${r.recallRef || ""}</td>
        <td>${formatCurrency(r.amount || 0)}</td>
        <td>${recallSheetDesc}</td>
      </tr>`;
    })
    .join("")}
  </tbody></table>`
      : `<div class="text-gray-500">No commission recalls recorded for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Builder Repayment (Payment to Builder)</div>

  ${
    builderRepaymentEFTs && builderRepaymentEFTs.length > 0
      ? `<table><thead><tr>
    <th>EFT Date</th><th>Builder</th><th>EFT #</th><th>Amount</th><th>Description</th>
  </tr></thead><tbody>
  ${builderRepaymentEFTs
    .map((eft) => {
      const payDate =
        eft.chequeDate || eft.date
          ? formatDate(eft.chequeDate || eft.date)
          : "-";
      const sheetDesc =
        eft.description ||
        `Trade #: ${trade.tradeNumber || trade.keyInfo?.tradeNumber || ""}, Builder repayment - ${eft.recipient || ""}`;
      return `<tr>
        <td>${payDate}</td>
        <td>${eft.recipient || "-"}</td>
        <td>${eft.eftNumber || ""}</td>
        <td>${formatCurrency(eft.amount || 0)}</td>
        <td>${sheetDesc}</td>
      </tr>`;
    })
    .join("")}
  </tbody></table>`
      : `<div class="text-gray-500">No builder repayments recorded for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">General to Commission Transfer</div>

  ${
    generalToCommissionEFTs && generalToCommissionEFTs.length > 0
      ? `<table><thead><tr>
    <th>EFT Date</th><th>EFT #</th><th>Amount</th><th>Description</th>
  </tr></thead><tbody>
  ${generalToCommissionEFTs
    .map((eft) => {
      const payDate =
        eft.chequeDate || eft.date
          ? formatDate(eft.chequeDate || eft.date)
          : "-";
      const sheetDesc =
        eft.description ||
        `Trade #: ${trade.tradeNumber || trade.keyInfo?.tradeNumber || ""}, Commission transfer from General to Commission`;
      return `<tr>
        <td>${payDate}</td>
        <td>${eft.eftNumber || ""}</td>
        <td>${formatCurrency(eft.amount || 0)}</td>
        <td>${sheetDesc}</td>
      </tr>`;
    })
    .join("")}
  </tbody></table>`
      : `<div class="text-gray-500">No general to commission transfers recorded for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Commission Trust Agreement</div>

  <div style="margin-bottom: 15px; font-size: 10px; line-height: 1.4;">
    It is understood between all parties that this agreement shall constitute a Commission Trust Agreement as set out in the contract.
  </div>

  <div style="margin-bottom: 15px; font-size: 10px; line-height: 1.4;">
    To the best of my knowledge the above information is correct. Dated at Markham on ${currentDate}
  </div>

  ${(agentsForTrustAgreement && agentsForTrustAgreement.length > 0
    ? agentsForTrustAgreement
    : []
  )
    .map(
      (agent) => `
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
    <div style="font-size: 10px;">
      ${agent.agentName || "Agent Name"}, ${agent.agentId ?? "Agent Number"}
    </div>
    <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
  </div>
`,
    )
    .join("")}
  ${
    !agentsForTrustAgreement || agentsForTrustAgreement.length === 0
      ? `
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
    <div style="font-size: 10px;">Agent Name, Agent Number</div>
    <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
  </div>
`
      : ""
  }

  <div style="display: flex; justify-content: space-between; align-items: center;">
    <div style="font-size: 10px;">
      Broker of Record: ABC Realty Inc., Brokerage
    </div>

    <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
  </div>

</div>

        ${(() => {
          // Calculate YTD Income Details for the agent - only show if trade is finalized

          return ""; // YTD Income Details removed

          const agentsForYTD =
            agentDetailsForPrint && agentDetailsForPrint.length > 0
              ? agentDetailsForPrint
              : (agentCalculations || []).filter(
                  (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
                );
          if (agentYTDEfts.length > 0 && agentsForYTD.length > 0) {
            const agentId = agentsForYTD[0].agentId;

            const agentName = agentsForYTD[0].agentName;

            // Calculate totals from YTD EFTs

            const totalNetCommission = agentYTDEfts.reduce(
              (sum, eft) => sum + parseFloat(eft.amount || 0),

              0,
            );

            // Calculate fees and buyer rebate from all trades associated with YTD EFTs

            let totalFees = 0;

            let totalBuyerRebate = 0;

            let totalHST = 0;

            // Sum fees, buyer rebates, and HST from each trade's agent commission list

            agentYTDEfts.forEach((eft) => {
              const eftTrade = eft.tradeData || eft.tradeId; // Use attached tradeData or fallback to tradeId

              const netCommission = parseFloat(eft.amount || 0);

              // HST portion of tax-inclusive net commission (same as Agent Summary)
              const hst = netCommission * INCLUSIVE_HST_FRACTION;

              totalHST += hst;

              if (eftTrade && eftTrade.agentCommissionList) {
                // Find agent commission data - try multiple matching strategies

                let tradeAgent = eftTrade.agentCommissionList.find(
                  (ac) => ac.agentId === agentId || ac.agentName === agentName,
                );

                if (!tradeAgent && agentInfoForYTD?.employeeNo) {
                  tradeAgent = eftTrade.agentCommissionList.find(
                    (ac) =>
                      ac.agentId === agentInfoForYTD.employeeNo.toString(),
                  );
                }

                if (!tradeAgent && eftTrade.agentCommissionList.length === 1) {
                  tradeAgent = eftTrade.agentCommissionList[0];
                }

                if (tradeAgent) {
                  // Use totalFees if available, otherwise calculate fees based on agent fee plan

                  let feesDeducted = parseFloat(
                    tradeAgent.totalFees || tradeAgent.feesDeducted || 0,
                  );

                  // If fees not found, calculate based on agent fee plan

                  if (!feesDeducted && agentInfoForYTD?.feeInfo) {
                    const feePlan = agentInfoForYTD.feeInfo;

                    let baseFee = 0;

                    switch (feePlan) {
                      case "plan250":
                        baseFee = 250;

                        break;

                      case "plan500":
                        baseFee = 500;

                        break;

                      case "plan9010":
                        baseFee = netCommission * 0.1;

                        break;

                      case "plan955":
                        baseFee = netCommission * 0.05;

                        break;

                      case "plan8515":
                        baseFee = netCommission * 0.15;

                        break;

                      case "plan5050":
                        baseFee = netCommission * 0.5;

                        break;

                      case "plan8020":
                        baseFee = netCommission * 0.2;

                        break;

                      case "plan150":
                        baseFee = 150;

                        break;

                      default:
                        baseFee = 0;
                    }

                    const taxRateYTD =
                      eftTrade?.customTaxRate != null
                        ? eftTrade.customTaxRate
                        : DEFAULT_TAX_RATE;
                    const taxOnFees = baseFee * taxRateYTD;

                    feesDeducted = baseFee + taxOnFees;
                  }

                  totalFees += feesDeducted;

                  // Get buyer rebate if included

                  if (
                    tradeAgent.buyerRebateIncluded === "yes" &&
                    tradeAgent.buyerRebateAmount
                  ) {
                    totalBuyerRebate += parseFloat(
                      tradeAgent.buyerRebateAmount || 0,
                    );
                  }
                }
              }
            });

            const totalIncludingHST = totalNetCommission;

            const grossEarnings = totalNetCommission + totalFees;

            // NET COMMISSION = TOTAL INCLUDING HST / 113 * 100

            const netCommission = (totalIncludingHST / 113) * 100;

            // HST PAID = NET COMMISSION * 13 / 100

            const hstPaid = netCommission * (13 / 100);

            return `
<div class="section">

<div class="section-title">YTD Income Details</div>

<div style="margin-top: 15px; font-size: 11px; line-height: 1.6;">

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">Gross Earnings(HST Included):</span>
<span>${formatCurrency(grossEarnings)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">Office Share( HST Included):</span>
<span>${formatCurrency(totalFees)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">Net Commission(HST Excluded):</span>
<span>${formatCurrency(netCommission)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">HST Paid To Agent:</span>
<span>${formatCurrency(hstPaid)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">TOTAL INCLUDING HST:</span>
<span>${formatCurrency(totalIncludingHST)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">BUYER REBATE:</span>
<span>${formatCurrency(-totalBuyerRebate)}</span>
</div>

</div>

</div>           
            `;
          }

          return "";
        })()}
      </body>
      </html>
    `;
      printWindow.document.write(printContent);
      printWindow.document.close();
      // ALSO generate PDF blob for Dropbox upload (done in background)
      try {
        const pdfIframe = document.createElement("iframe");
        pdfIframe.style.position = "absolute";
        pdfIframe.style.width = "0";
        pdfIframe.style.height = "0";
        pdfIframe.style.border = "none";
        document.body.appendChild(pdfIframe);
        const pdfDoc = pdfIframe.contentWindow.document;
        pdfDoc.open();
        pdfDoc.write(printContent);
        pdfDoc.close();
        // Wait a bit for content to load
        setTimeout(async () => {
          try {
            // Generate PDF using our utility
            const pdfBlob = await generatePDFFromElement(
              pdfDoc.body,
              `Trade_${trade.tradeNumber}.pdf`,
            );
            // Convert to base64 and store
            const base64 = await blobToBase64(pdfBlob);
            setLastGeneratedPDF({
              base64: `data:application/pdf;base64,${base64}`,
              fileName: `Trade_${trade.tradeNumber}_${Date.now()}.pdf`,
              size: pdfBlob.size,
              timestamp: Date.now(),
            });
            console.log("? PDF generated and cached for Dropbox upload");
            // Clean up iframe
            if (pdfIframe.parentNode) {
              document.body.removeChild(pdfIframe);
            }
          } catch (pdfError) {
            console.error("PDF generation for cache failed:", pdfError);
            // Don't show error to user, just log it
          }
        }, 1000);
      } catch (cacheError) {
        console.error("Failed to cache PDF:", cacheError);
        // Silent fail - doesn't affect printing
      }
      // Wait for content to load then automatically print
      setTimeout(() => {
        try {
          printWindow.print();
          // Close the window after a delay to allow printing to complete
          if (!useCurrentWindow) {
            setTimeout(() => {
              printWindow.close();
            }, 1000);
          }
          // Show success message
          if (!useCurrentWindow) {
            setTimeout(() => {
              alert("Print dialog opened successfully!");
            }, 100);
          }
        } catch (printError) {
          console.error("Print error:", printError);
          // If automatic print fails, keep the window open for manual printing
          if (!useCurrentWindow) {
            printWindow.focus();
          }
          alert(
            "Print dialog failed to open. The report is ready for manual printing.",
          );
        }
      }, 500);
    } catch (error) {
      console.error("Error generating print report:", error);
      alert("Error generating print report. Please try again.");
    } finally {
      setIsPrinting(false);
    }
  };
  // Dropbox authentication handler
  const handleDropboxAuth = async () => {
    try {
      // Automatically detect the environment and use appropriate redirect URI
      const redirectUri =
        window.location.hostname === "abc.brokercore.app"
          ? "https://abc.brokercore.app/api/dropbox/oauth/callback"
          : "http://localhost:5001/api/dropbox/oauth/callback";
      console.log("?? Dropbox Auth Debug:");
      console.log("  - Backend redirect URI:", redirectUri);
      console.log("  - Frontend origin:", window.location.origin);
      const response = await axiosInstance.get("/dropbox/auth-url", {
        params: { redirectUri },
      });
      console.log("  - Auth URL received:", response.data.authUrl);
      console.log("  - Server using redirect URI:", response.data.redirectUri);
      console.log(
        "\n?? If you see an error, make sure this redirect URI is added to Dropbox:",
      );
      console.log("  ", response.data.redirectUri || redirectUri);
      if (response.data.success) {
        console.log("?? Redirecting to Dropbox for authentication...");
        // Direct navigation to Dropbox auth - tokens will be stored by the callback page
        window.location.href = response.data.authUrl;
      } else {
        alert(
          "Failed to get Dropbox authentication URL. Please check your Dropbox app configuration.",
        );
      }
    } catch (error) {
      console.error("Error getting Dropbox auth URL:", error);
      let errorMessage = "Failed to initiate Dropbox authentication.\n\n";
      if (error.response?.status === 500) {
        errorMessage += "?? Backend Configuration Issue:\n";
        errorMessage += "The Dropbox App Key is not configured.\n\n";
        errorMessage += "Please update your backend/.env file with:\n";
        errorMessage += "DROPBOX_APP_KEY=5yi0val1umep7x7b\n";
        errorMessage += "DROPBOX_APP_SECRET=ja3j1pr1d9v89u3\n";
        errorMessage +=
          "DROPBOX_REDIRECT_URI=" +
          window.location.origin +
          "/api/dropbox/oauth/callback\n\n";
        errorMessage += "Then restart the backend server.";
      } else if (error.response?.data?.message) {
        errorMessage += "Error: " + error.response.data.message;
      } else {
        errorMessage += "Error: " + (error.message || "Unknown error");
        errorMessage += "\n\nPlease check:\n";
        errorMessage += "1. Backend server is running\n";
        errorMessage += "2. Dropbox credentials are configured in .env\n";
        errorMessage += "3. Backend console for detailed errors";
      }
      alert(errorMessage);
    }
  };
  // Fetch Dropbox folders
  const fetchDropboxFolders = async (path = "") => {
    setLoadingFolders(true);
    const token = localStorage.getItem("dropboxAccessToken");
    if (!token) {
      console.error("? No Dropbox access token found");
      alert("Please authenticate with Dropbox first.");
      setLoadingFolders(false);
      return;
    }
    try {
      const response = await axiosInstance.post("/dropbox/list-folder", {
        accessToken: token,
        path: path || "",
      });
      console.log("?? Dropbox folders response:", response.data);
      if (response.data.success && response.data.entries) {
        // Filter only folders
        const folders = response.data.entries.filter(
          (entry) => entry[".tag"] === "folder",
        );
        setDropboxFolders(folders);
        console.log(`? Found ${folders.length} folders`);
      } else {
        console.error("? No entries in response:", response.data);
        setDropboxFolders([]);
      }
    } catch (error) {
      console.error("? Error fetching folders:", error);
      const errorMsg =
        error.response?.data?.message ||
        "Failed to load folders. Please try again.";
      alert(errorMsg);
      setDropboxFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  };
  // Dropbox upload - allow user to select any file and upload to chosen folder
  const handleDropboxUpload = async () => {
    if (isUploadingToDropbox) return;
    // ALWAYS use localStorage as source of truth for token
    let currentToken = localStorage.getItem("dropboxAccessToken");
    console.log("?? Upload Debug - Detailed:", {
      stateToken: dropboxAccessToken ? "EXISTS" : "NULL",
      localStorageToken: currentToken
        ? currentToken.substring(0, 20) + "..."
        : "NULL",
      hasToken: !!currentToken,
      allLocalStorage: {
        accessToken: localStorage.getItem("dropboxAccessToken")
          ? "EXISTS"
          : "NULL",
        refreshToken: localStorage.getItem("dropboxRefreshToken")
          ? "EXISTS"
          : "NULL",
      },
    });
    // Update state if token exists in localStorage but not in state (for UI consistency)
    if (currentToken && !dropboxAccessToken) {
      console.log("?? Syncing state from localStorage...");
      setDropboxAccessToken(currentToken);
      const refreshToken = localStorage.getItem("dropboxRefreshToken");
      if (refreshToken) {
        setDropboxRefreshToken(refreshToken);
      }
    }
    // Verify authentication using localStorage (not state)
    if (!currentToken) {
      console.error("? No token found in localStorage!");
      console.log("?? All localStorage keys:", Object.keys(localStorage));
      alert(
        "Please connect to Dropbox first using the 'Connect Dropbox' button.\n\nIf you just connected, try refreshing the page.",
      );
      return;
    }
    console.log("? Authenticated! Opening file picker...");
    // Create file input element
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "*/*"; // Accept all file types
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      // Store the selected file and show folder browser
      setSelectedFile(file);
      setCurrentPath("");
      setSelectedFolder("");
      fetchDropboxFolders("");
      setShowFolderBrowser(true);
    };
    // Trigger file selection
    fileInput.click();
  };
  // Handle actual upload after folder is selected
  const handleConfirmUpload = async () => {
    if (!selectedFile) return;
    const folderPath = selectedFolder || "";
    setShowFolderBrowser(false);
    setIsUploadingToDropbox(true);
    try {
      console.log("?? Uploading file to Dropbox...");
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64Data = event.target.result.split(",")[1];
          const fileName = selectedFile.name;
          const uploadPath = folderPath
            ? `${folderPath}/${fileName}`
            : `/${fileName}`;
          console.log(`Uploading: ${fileName} to ${uploadPath}`);
          // Always use localStorage token as source of truth
          let currentAccessToken = localStorage.getItem("dropboxAccessToken");
          // Try to upload with current token
          try {
            const response = await axiosInstance.post(
              "/dropbox/upload-pdf-to-dropbox",
              {
                pdfBase64: `data:${selectedFile.type};base64,${base64Data}`,
                fileName: fileName,
                accessToken: currentAccessToken,
                folderPath: uploadPath,
              },
            );
            if (response.data.success) {
              alert(
                `? SUCCESS!\n\nFile uploaded to Dropbox!\n\nFile: ${
                  response.data.file.name
                }\nSize: ${Math.round(selectedFile.size / 1024)} KB\nPath: ${
                  response.data.file.path
                }`,
              );
              setIsUploadingToDropbox(false);
              return;
            }
          } catch (uploadError) {
            // Check if token expired
            if (
              uploadError.response?.status === 401 ||
              uploadError.response?.data?.needsTokenRefresh
            ) {
              console.log("?? Token expired, refreshing...");
              const refreshToken = localStorage.getItem("dropboxRefreshToken");
              if (refreshToken) {
                try {
                  const refreshResponse = await axiosInstance.post(
                    "/dropbox/refresh-token",
                    {
                      refreshToken: refreshToken,
                    },
                  );
                  if (refreshResponse.data.success) {
                    const newAccessToken = refreshResponse.data.accessToken;
                    localStorage.setItem("dropboxAccessToken", newAccessToken);
                    setDropboxAccessToken(newAccessToken);
                    console.log("? Token refreshed, retrying upload...");
                    // Retry with new token
                    const retryResponse = await axiosInstance.post(
                      "/api/dropbox/upload-pdf-to-dropbox",
                      {
                        pdfBase64: `data:${selectedFile.type};base64,${base64Data}`,
                        fileName: fileName,
                        accessToken: newAccessToken,
                        folderPath: uploadPath,
                      },
                    );
                    if (retryResponse.data.success) {
                      alert(
                        `? SUCCESS!\n\nFile uploaded to Dropbox!\n\nFile: ${
                          retryResponse.data.file.name
                        }\nSize: ${Math.round(selectedFile.size / 1024)} KB\nPath: ${
                          retryResponse.data.file.path
                        }`,
                      );
                      setIsUploadingToDropbox(false);
                      return;
                    }
                  }
                } catch (refreshError) {
                  console.error("Token refresh failed:", refreshError);
                  localStorage.removeItem("dropboxAccessToken");
                  localStorage.removeItem("dropboxRefreshToken");
                  setDropboxAccessToken(null);
                  setDropboxRefreshToken(null);
                  alert(
                    '?? Session expired. Please click "Upload to Dropbox" to re-authenticate.',
                  );
                  setIsUploadingToDropbox(false);
                  return;
                }
              } else {
                localStorage.removeItem("dropboxAccessToken");
                setDropboxAccessToken(null);
                alert(
                  '?? Session expired. Please click "Upload to Dropbox" again to re-authenticate.',
                );
                setIsUploadingToDropbox(false);
                return;
              }
            }
            // If not 401, show the error
            console.error("Upload error:", uploadError);
            alert(
              `? Upload failed: ${
                uploadError.response?.data?.message || uploadError.message
              }`,
            );
            setIsUploadingToDropbox(false);
          }
        } catch (error) {
          console.error("File processing error:", error);
          alert(`? Error: ${error.message || "Failed to process file"}`);
          setIsUploadingToDropbox(false);
        }
      };
      reader.onerror = () => {
        alert("? Error reading file");
        setIsUploadingToDropbox(false);
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error("File upload error:", error);
      alert(`? Error: ${error.message || "Failed to upload file"}`);
      setIsUploadingToDropbox(false);
    }
  };
  const handleDropboxUpload_OLD = async () => {
    if (isUploadingToDropbox) return;
    // Check if user is authenticated
    if (!dropboxAccessToken) {
      const shouldAuth = window.confirm(
        "You need to authenticate with Dropbox first. Would you like to do that now?",
      );
      if (shouldAuth) {
        handleDropboxAuth();
      }
      return;
    }
    setIsUploadingToDropbox(true);
    try {
      console.log("?? Generating PDF for Dropbox upload...");
      // Create invisible iframe for PDF generation
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      document.body.appendChild(iframe);
      const iframeDoc = iframe.contentWindow.document;
      const currentDate = new Date().toLocaleDateString();
      const selectedAgentIndex = null;
      // For iframe-based PDF generation, we don't need popup windows
      const useCurrentWindow = false;
      // Fetch EFTs
      let realEstateTrustEFTs = [];
      let commissionTrustEFTs = [];
      let eftReversals = [];
      let ledgerEntries = [];
      let agentYTDEfts = []; // All agent commission EFTs for YTD calculation
      let agentInfoForYTD = null; // Agent info for fee calculation
      if (trade && trade._id) {
        try {
          // Get agent ID from trade for YTD calculation
          const agentId = trade.agentCommissionList?.[0]?.agentId;
          const agentName = trade.agentCommissionList?.[0]?.agentName;
          // Fetch all commission trust EFTs for YTD (not just for this trade)
          if (agentId || agentName) {
            try {
              // Fetch with populated trade data, agent info, and all trades
              const [allEFTsRes, agentsRes, tradesRes] = await Promise.all([
                axiosInstance.get("/commission-trust-eft"),
                axiosInstance.get("/agents"),
                axiosInstance.get("/trades"),
              ]);
              const allCommissionEFTs = allEFTsRes.data || [];
              const agents = agentsRes.data || [];
              const allTrades = tradesRes.data || [];
              // Find agent info for fee calculation
              agentInfoForYTD = agents.find(
                (a) =>
                  a.employeeNo?.toString() === agentId || a._id === agentId,
              );
              // Get current trade number
              const currentTradeNumber = parseInt(
                trade.tradeNumber || trade.keyInfo?.tradeNumber || 0,
              );
              // Get the year from the current trade's finalized date for YTD calculation
              // YTD runs from Jan 1 to Dec 31 of the trade's finalized year, resetting on Jan 1st
              const currentTradeFinalizedDate = trade.keyInfo?.finalizedDate
                ? new Date(trade.keyInfo.finalizedDate)
                : null;
              const currentYear = currentTradeFinalizedDate
                ? currentTradeFinalizedDate.getFullYear()
                : new Date().getFullYear();
              // Filter: include all finalized trades for this agent with trade number <= current trade number
              agentYTDEfts = allCommissionEFTs
                .filter((eft) => {
                  if (eft.type !== "AgentCommissionTransfer") return false;
                  const matchesAgent =
                    eft.agentId === agentId || eft.agentName === agentName;
                  if (!matchesAgent) return false;
                  // Find the trade for this EFT
                  const eftTradeId = eft.tradeId?._id || eft.tradeId;
                  const eftTrade = allTrades.find((t) => t._id === eftTradeId);
                  if (!eftTrade || !eftTrade.isFinalized) return false;
                  // Get trade number and check if it's <= current trade number
                  const eftTradeNumber = parseInt(
                    eftTrade.tradeNumber || eftTrade.keyInfo?.tradeNumber || 0,
                  );
                  // Check if finalized in the same year as the current trade
                  const eftFinalizedDate = eftTrade.keyInfo?.finalizedDate
                    ? new Date(eftTrade.keyInfo.finalizedDate)
                    : null;
                  const isInSameYear =
                    eftFinalizedDate &&
                    eftFinalizedDate.getFullYear() === currentYear;
                  return isInSameYear && eftTradeNumber <= currentTradeNumber;
                })
                .map((eft) => {
                  // Attach full trade data to each EFT for buyer rebate calculation
                  const tradeData = allTrades.find(
                    (t) => t._id === eft.tradeId || t._id === eft.tradeId?._id,
                  );
                  return { ...eft, tradeData };
                });
            } catch (err) {
              console.error("Error fetching YTD EFTs:", err);
            }
          }
          const [retResponse, ctResponse, reversalsRes, ledgerRes] =
            await Promise.all([
              axiosInstance.get(`/real-estate-trust-eft/trade/${trade._id}`),
              axiosInstance.get(`/commission-trust-eft/trade/${trade._id}`),
              axiosInstance.get(`/eft-reversals`, {
                params: { tradeId: trade._id },
              }),
              axiosInstance.get("/ledger"),
            ]);
          realEstateTrustEFTs = retResponse.data || [];
          commissionTrustEFTs = ctResponse.data || [];
          eftReversals = reversalsRes.data || [];
          // Get ledger entries and filter for this trade
          const allLedgerEntries = ledgerRes.data || [];
          const tradeNumber = trade.tradeNumber || trade.keyInfo?.tradeNumber;
          ledgerEntries = allLedgerEntries.filter(
            (entry) =>
              entry.tradeNumber === tradeNumber ||
              (entry.description &&
                entry.description.includes(`Trade #: ${tradeNumber}`)),
          );
        } catch (err) {
          console.error("Error fetching EFTs:", err);
        }
      }
      const commissionIncome =
        trade.commission?.commissionIncomeRows?.[0] || {};
      const baseOfficeCommission = "";
      // Get the trade classification
      const tradeClassification = trade.keyInfo?.classification || "";
      const isCoOperatingSide = tradeClassification === "CO-OPERATING SIDE";
      const taxRate =
        trade.customTaxRate != null ? trade.customTaxRate : DEFAULT_TAX_RATE;
      // Calculate Sub-Total and HST for Commission row
      const listingAmount =
        commissionIncome.listingAmount && commissionIncome.listingAmount !== ""
          ? parseFloat(commissionIncome.listingAmount)
          : 0;
      const sellingAmount =
        commissionIncome.sellingAmount && commissionIncome.sellingAmount !== ""
          ? parseFloat(commissionIncome.sellingAmount)
          : 0;
      const subTotal = (listingAmount + sellingAmount).toFixed(2);
      // Calculate HST based on the amounts (use trade's set tax rate when applied)
      const listingTax = (listingAmount * taxRate).toFixed(2);
      const sellingTax = (sellingAmount * taxRate).toFixed(2);
      const hst = (parseFloat(listingTax) + parseFloat(sellingTax)).toFixed(2);
      const total = (parseFloat(subTotal) + parseFloat(hst)).toFixed(2);
      // Get first outside broker row (from commission or outsideBrokers)
      let outsideBrokerInfo = null;
      if (trade.commission?.outsideBrokersRows?.length > 0) {
        outsideBrokerInfo = trade.commission.outsideBrokersRows[0];
      } else if (trade.outsideBrokers?.length > 0) {
        outsideBrokerInfo = trade.outsideBrokers[0];
      }
      // Determine side and amounts
      let obListing = "0.00",
        obSelling = "0.00",
        obSubTotal = "0.00",
        obTax = "0.00",
        obTotal = "0.00",
        obName = "";
      if (outsideBrokerInfo) {
        // Get the broker name
        const brokerName = `${
          outsideBrokerInfo.agentName || outsideBrokerInfo.firstName || ""
        } ${outsideBrokerInfo.lastName || ""}`.trim();
        // Get the company name
        const companyName =
          outsideBrokerInfo.company || outsideBrokerInfo.brokerage || "";
        // Combine name and company
        obName =
          brokerName && companyName
            ? `${brokerName} - ${companyName}`
            : brokerName || companyName || "Outside Broker";
        // Get the selling amount from either source
        const brokerSellingAmount = outsideBrokerInfo.sellingAmount || "0.00";
        // First check the broker's end from outsideBrokers array
        const matchingBrokerInOB = trade.outsideBrokers?.find((ob) => {
          const outsideBrokerName = `${ob.firstName} ${ob.lastName}`.trim();
          return (
            outsideBrokerName ===
              (outsideBrokerInfo.agentName || outsideBrokerInfo.firstName) ||
            ob.company === outsideBrokerInfo.company
          );
        });
        // Determine the end type from either the matching broker or current broker info
        let endType = "";
        if (matchingBrokerInOB) {
          if (
            matchingBrokerInOB.end?.toLowerCase().includes("listing") ||
            matchingBrokerInOB.type?.toLowerCase().includes("listing")
          ) {
            endType = "listing";
          } else {
            endType = "selling";
          }
        } else {
          const end = (
            outsideBrokerInfo.end ||
            outsideBrokerInfo.type ||
            ""
          ).toLowerCase();
          endType = end.includes("listing") ? "listing" : "selling";
        }
        // Place amount in appropriate column based on end type
        if (endType === "listing") {
          obListing = brokerSellingAmount;
          obSelling = "0.00";
        } else {
          obListing = "0.00";
          obSelling = brokerSellingAmount;
        }
        obSubTotal = brokerSellingAmount;
        obTax = outsideBrokerInfo.tax || "0.00";
        obTotal = outsideBrokerInfo.total || "0.00";
      }
      // Agent double ender: both sides in-house — show $0 on Outside Broker row; base office uses full commission
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        obListing = "0.00";
        obSelling = "0.00";
        obSubTotal = "0.00";
        obTax = "0.00";
        obTotal = "0.00";
      }
      // Referral agents (from Agent Info) appear under Expenses and reduce Base Office Commission
      const referralForExpenses = (trade.agentCommissionList || []).filter(
        (a) => (a.classification || "").toUpperCase() === "REFERRAL",
      );
      const referralAmountSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.amount || 0),
        0,
      );
      const referralTaxSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.tax || 0),
        0,
      );
      const referralTotalSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.total || 0),
        0,
      );
      const expensesRowsForPrint = [];
      if (outsideBrokerInfo) {
        expensesRowsForPrint.push({
          name: obName,
          listing: obListing,
          selling: obSelling,
          subTotal: obSubTotal,
          tax: obTax,
          total: obTotal,
        });
      }
      referralForExpenses.forEach((a) => {
        expensesRowsForPrint.push({
          name: `${a.agentName || ""} - Referral`,
          listing: "0.00",
          selling: a.amount || "0",
          subTotal: a.amount || "0",
          tax: a.tax || "0",
          total: a.total || "0",
        });
      });
      // Calculate Base Office Commission
      let baseOfficeCommissionListing,
        baseOfficeCommissionSelling,
        baseOfficeCommissionSubTotal,
        baseOfficeCommissionHST,
        baseOfficeCommissionTotal;
      if (isCoOperatingSide) {
        // For Co-operating Side, base office commission = commission (same values) unless there are referral agents (then deduct referral)
        if (referralForExpenses.length === 0) {
          baseOfficeCommissionListing = listingAmount;
          baseOfficeCommissionSelling = sellingAmount;
          baseOfficeCommissionSubTotal = subTotal;
          baseOfficeCommissionHST = hst;
          baseOfficeCommissionTotal = total;
        } else {
          baseOfficeCommissionListing = listingAmount;
          baseOfficeCommissionSelling = (
            parseFloat(sellingAmount) - referralAmountSum
          ).toFixed(2);
          baseOfficeCommissionSubTotal = (
            parseFloat(subTotal) - referralAmountSum
          ).toFixed(2);
          baseOfficeCommissionHST = (parseFloat(hst) - referralTaxSum).toFixed(
            2,
          );
          baseOfficeCommissionTotal = (
            parseFloat(total) - referralTotalSum
          ).toFixed(2);
        }
      } else {
        // For other classifications, base office commission is the net after outside broker + referral deductions
        baseOfficeCommissionListing = (
          parseFloat(listingAmount) - parseFloat(obListing)
        ).toFixed(2);
        baseOfficeCommissionSelling = (
          parseFloat(sellingAmount) -
          parseFloat(obSelling) -
          referralAmountSum
        ).toFixed(2);
        baseOfficeCommissionSubTotal = (
          parseFloat(subTotal) -
          parseFloat(obSubTotal) -
          referralAmountSum
        ).toFixed(2);
        baseOfficeCommissionHST = (
          parseFloat(hst) -
          parseFloat(obTax) -
          referralTaxSum
        ).toFixed(2);
        baseOfficeCommissionTotal = (
          parseFloat(total) -
          parseFloat(obTotal) -
          referralTotalSum
        ).toFixed(2);
      }
      // Calculate Agent Commission for all agents
      // For AGENT DOUBLE ENDER, track transaction order per agent
      const agentDoubleEnderOrder = new Map();
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        (trade.agentCommissionList || []).forEach((a, index) => {
          if (a.classification === "AGENT DOUBLE ENDER" && a.agentId) {
            const key = a.agentId;
            if (!agentDoubleEnderOrder.has(key)) {
              agentDoubleEnderOrder.set(key, []);
            }
            agentDoubleEnderOrder.get(key).push(index);
          }
        });
      }
      const agentCalculations = (trade.agentCommissionList || []).map(
        (agent, agentIndex) => {
          const agentAmount =
            agent.amount && agent.amount !== "" ? parseFloat(agent.amount) : 0;
          const agentFeesDeducted =
            agent.feesDeducted && agent.feesDeducted !== ""
              ? parseFloat(agent.feesDeducted)
              : 0;
          const agentClassification = agent.classification?.toLowerCase() || "";
          const buyerRebateAmount =
            isBuyerRebateIncludedYes(agent) && agent.buyerRebateAmount
              ? parseFloat(agent.buyerRebateAmount) || 0
              : 0;
          const shareAfterPlan = agentAmount - agentFeesDeducted;
          const displaySubForSheet = Math.max(
            0,
            shareAfterPlan - buyerRebateAmount,
          );
          // Determine agent amounts based on trade classification (not agent classification)
          let agentListing = "0.00";
          let agentSelling = "0.00";
          // For AGENT DOUBLE ENDER: use transactionType to determine which column
          if (
            tradeClassification === "AGENT DOUBLE ENDER" &&
            agent.classification === "AGENT DOUBLE ENDER"
          ) {
            // Determine transaction type - use saved value or infer from order
            let transactionType = agent.transactionType;
            // If transactionType is missing, infer from order
            if (!transactionType && agent.agentId) {
              const agentIndices =
                agentDoubleEnderOrder.get(agent.agentId) || [];
              const positionInList = agentIndices.indexOf(agentIndex);
              // First occurrence (position 0) = LISTING, second (position 1) = SELLING
              transactionType = positionInList === 0 ? "LISTING" : "SELLING";
            }
            if (transactionType === "LISTING") {
              // First transaction goes to Listing column
              agentListing = displaySubForSheet.toFixed(2);
              agentSelling = "0.00";
            } else if (transactionType === "SELLING") {
              // Second transaction goes to Selling column
              agentListing = "0.00";
              agentSelling = displaySubForSheet.toFixed(2);
            }
          } else if (isCoOperatingSide) {
            // For CO-OPERATING SIDE: put agent amount in selling column
            agentListing = "0.00";
            agentSelling = displaySubForSheet.toFixed(2);
          } else {
            // For LISTING SIDE: put agent amount in listing column
            agentListing = displaySubForSheet.toFixed(2);
            agentSelling = "0.00";
          }
          // Sub-Total column; Total / HST match Agent Info (net = amount + tax − totalFees).
          let agentSubTotal = (
            parseFloat(agentListing) + parseFloat(agentSelling)
          ).toFixed(2);
          const { agentTotal, agentHST } =
            getAgentFinancialTotalsBeforeOfficeExp(
              agent,
              parseFloat(agentSubTotal),
              taxRate,
              buyerRebateAmount,
            );
          const officeExpenseFinancialRows = buildOfficeExpenseFinancialRows(
            agent,
            tradeClassification,
            taxRate,
          );
          return {
            ...agent,
            agentListing,
            agentSelling,
            agentSubTotal,
            agentHST: agentHST.toFixed(2),
            agentTotal: agentTotal.toFixed(2),
            officeExpenseFinancialRows,
          };
        },
      );
      const showBaseOfficeAndNetToOffice =
        selectedAgentIndex === null || selectedAgentIndex === undefined;
      // Modal passes index into non-referral agents only; match by that list (by name/identity), not by order in full list.
      const nonReferralAgents = agentCalculations.filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      let agentCalculationsFiltered = agentCalculations;
      if (!showBaseOfficeAndNetToOffice && selectedAgentIndex != null) {
        const indices = Array.isArray(selectedAgentIndex)
          ? selectedAgentIndex
          : [selectedAgentIndex];
        const selected = indices
          .filter((i) => Number.isInteger(i) && nonReferralAgents[i])
          .map((i) => nonReferralAgents[i]);
        if (selected.length > 0) {
          agentCalculationsFiltered = selected;
        }
      }
      // Exclude referral agents from Net to Office agent sum (referral already deducted in Base Office Commission).
      // Net to Office: sub-total + HST on net sub-total (sheet Total excludes rebate add-back; EFT adds rebates separately).
      const agentsForNetToOffice = agentCalculationsFiltered.filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      const agentKeysForBuyerRebateSheet = showBaseOfficeAndNetToOffice
        ? null
        : new Set(
            agentsForNetToOffice.map((a) =>
              String(a.agentId ?? a.agentName ?? ""),
            ),
          );
      const buyerRebateCols = getBuyerRebateListingSellingForSheet(trade, {
        agentKeys: agentKeysForBuyerRebateSheet,
      });
      const buyerRebateListing = buyerRebateCols.listing.toFixed(2);
      const buyerRebateSelling = buyerRebateCols.selling.toFixed(2);
      const totalBuyerRebate = parseFloat(
        (buyerRebateCols.listing + buyerRebateCols.selling).toFixed(2),
      );
      let netToOfficeListing,
        netToOfficeSelling,
        netToOfficeSubTotal,
        netToOfficeHST,
        netToOfficeTotal;
      let totalAgentListing,
        totalAgentSelling,
        totalAgentSubTotal,
        totalAgentHST,
        totalAgentTotal;
      if (!isCoOperatingSide && agentsForNetToOffice.length === 2) {
        const agent1 = agentsForNetToOffice[0] || {};
        const agent2 = agentsForNetToOffice[1] || {};
        totalAgentListing =
          parseFloat(agent1.agentListing || 0) -
          parseFloat(agent2.agentListing || 0);
        totalAgentSelling =
          parseFloat(agent1.agentSelling || 0) -
          parseFloat(agent2.agentSelling || 0);
        totalAgentSubTotal =
          parseFloat(agent1.agentSubTotal || 0) -
          parseFloat(agent2.agentSubTotal || 0);
        totalAgentHST =
          parseFloat(agent1.agentHST || 0) - parseFloat(agent2.agentHST || 0);
        totalAgentTotal =
          parseFloat(agent1.agentTotal || 0) -
          parseFloat(agent2.agentTotal || 0);
      } else {
        totalAgentListing = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentListing || 0),
          0,
        );
        totalAgentSelling = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentSelling || 0),
          0,
        );
        totalAgentSubTotal = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentSubTotal || 0),
          0,
        );
        totalAgentHST = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentHST || 0),
          0,
        );
        totalAgentTotal = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentTotal || 0),
          0,
        );
      }
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        const totalListingToAgents = agentsForNetToOffice.reduce(
          (sum, a) => sum + parseFloat(a.agentListing || 0),
          0,
        );
        const totalSellingToAgents = agentsForNetToOffice.reduce(
          (sum, a) => sum + parseFloat(a.agentSelling || 0),
          0,
        );

        netToOfficeListing = (
          parseFloat(baseOfficeCommissionListing) -
          totalListingToAgents -
          buyerRebateCols.listing
        ).toFixed(2);

        netToOfficeSelling = (
          parseFloat(baseOfficeCommissionSelling) -
          totalSellingToAgents -
          buyerRebateCols.selling
        ).toFixed(2);

        netToOfficeSubTotal = (
          parseFloat(netToOfficeListing) + parseFloat(netToOfficeSelling)
        ).toFixed(2);
        netToOfficeHST = (parseFloat(netToOfficeSubTotal) * taxRate).toFixed(2);
        netToOfficeTotal = (
          parseFloat(netToOfficeSubTotal) + parseFloat(netToOfficeHST)
        ).toFixed(2);
      } else if (!isCoOperatingSide && agentsForNetToOffice.length === 2) {
        // Listing-side two-agent plan: sub-total = plan fees + office expenses;
        // HST = tax on plan fees + override HST to office + office expense tax.
        const totalPlanFees = agentsForNetToOffice.reduce(
          (total, agent) =>
            total +
            parseFloat(agent.feesDeducted || 0) +
            officeExpensesBaseForAgent(agent, taxRate),
          0,
        );
        const planFeesHST = agentsForNetToOffice.reduce(
          (total, agent) =>
            total +
            roundTo2((parseFloat(agent.feesDeducted || 0) || 0) * taxRate),
          0,
        );
        const overriddenHSTToOffice = agentsForNetToOffice.reduce(
          (total, agent) => {
            const isTaxOverridden =
              agent.taxOverridden === true || agent.taxOverridden === "true";
            return (
              total +
              (isTaxOverridden ? parseFloat(agent.amount || 0) * taxRate : 0) +
              officeExpensesTaxForAgent(agent, taxRate)
            );
          },
          0,
        );
        netToOfficeSubTotal = totalPlanFees.toFixed(2);
        netToOfficeHST = (planFeesHST + overriddenHSTToOffice).toFixed(2);
        netToOfficeTotal = (
          totalPlanFees +
          planFeesHST +
          overriddenHSTToOffice
        ).toFixed(2);
        // For listing side with 2 agents, distribute the amounts appropriately
        // Since this is listing side, put the amounts in listing column
        netToOfficeListing = netToOfficeSubTotal;
        netToOfficeSelling = "0.00";
      } else {
        netToOfficeListing = (
          parseFloat(baseOfficeCommissionListing) -
          totalAgentListing -
          buyerRebateCols.listing
        ).toFixed(2);
        netToOfficeSelling = (
          parseFloat(baseOfficeCommissionSelling) -
          totalAgentSelling -
          buyerRebateCols.selling
        ).toFixed(2);
        netToOfficeSubTotal = (
          parseFloat(netToOfficeListing) + parseFloat(netToOfficeSelling)
        ).toFixed(2);
        netToOfficeHST = (parseFloat(netToOfficeSubTotal) * taxRate).toFixed(2);
        netToOfficeTotal = (
          parseFloat(netToOfficeSubTotal) + parseFloat(netToOfficeHST)
        ).toFixed(2);
      }
      const isTwoAgentPlan =
        !isCoOperatingSide && agentsForNetToOffice.length === 2;
      if (!isTwoAgentPlan) {
        ({
          netToOfficeListing,
          netToOfficeSelling,
          netToOfficeSubTotal,
          netToOfficeHST,
          netToOfficeTotal,
        } = addOfficeExpensesToNetToOfficeValues(
          {
            netToOfficeListing: netToOfficeListing || "0.00",
            netToOfficeSelling: netToOfficeSelling || "0.00",
            netToOfficeSubTotal,
            netToOfficeHST,
            netToOfficeTotal,
          },
          agentsForNetToOffice,
          tradeClassification,
          taxRate,
        ));
      }
      // Format the outside brokers data (include referral agents from Agent Info for trade record sheet print)
      const getEndValueForPrintBroker = (b) => {
        const matchingBroker1 = trade.outsideBrokers?.find((ob) => {
          const outsideBrokerName = `${ob.firstName} ${ob.lastName}`.trim();
          return (
            outsideBrokerName === b.agentName || ob.company === b.brokerage
          );
        });
        if (matchingBroker1) {
          if (
            matchingBroker1.end?.toLowerCase().includes("listing") ||
            matchingBroker1.type?.toLowerCase().includes("listing")
          )
            return "Listing";
        }
        if (
          b.end?.toLowerCase().includes("listing") ||
          b.type?.toLowerCase().includes("listing")
        )
          return "Listing";
        return "Selling";
      };
      const formattedOutsideBrokersBase =
        trade.commission?.outsideBrokersRows?.map((broker) => ({
          agentName: broker.agentName || "-",
          brokerage: broker.brokerage || "-",
          end: getEndValueForPrintBroker(broker),
          percentage: broker.percentage,
          sellingAmount: broker.sellingAmount || "0.00",
          tax: broker.tax || "0.00",
          total: broker.total || "0.00",
        })) || [];
      const referralAgentsForPrint = (trade.agentCommissionList || []).filter(
        (a) => (a.classification || "").toUpperCase() === "REFERRAL",
      );
      const referralCommissionRowsForPrint = referralAgentsForPrint.map(
        (a) => ({
          agentName: a.agentName || "-",
          brokerage: a.agentName || "Referral",
          end: "Selling",
          percentage: a.percentage,
          sellingAmount: a.amount || "0.00",
          tax: a.tax || "0.00",
          total: a.total || "0.00",
        }),
      );
      const formattedOutsideBrokers = [
        ...formattedOutsideBrokersBase,
        ...referralCommissionRowsForPrint,
      ];
      const referralAsBrokersForPrint = referralAgentsForPrint.map((a) => ({
        type: "Referral",
        firstName: "-",
        lastName: "-",
        company: a.agentName || "Referral",
        end: "Selling End",
        primaryPhone: "-",
      }));
      const allOutsideBrokersForPrint = [
        ...(trade.outsideBrokers || []),
        ...referralAsBrokersForPrint,
      ];
      // Agent Details in print excludes referral agents (they appear under Outside Brokers)
      const agentDetailsForPrint = (agentCalculationsFiltered || []).filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      // ADE has LISTING + SELLING rows per agent — signatures need each person once
      const agentsForTrustAgreement = (() => {
        const byKey = new Map();
        for (const agent of agentDetailsForPrint) {
          const id =
            agent.agentId != null && String(agent.agentId).trim() !== ""
              ? String(agent.agentId).trim()
              : "";
          const name = String(agent.agentName || "")
            .trim()
            .toLowerCase();
          const key = id || name;
          if (!key || byKey.has(key)) continue;
          byKey.set(key, agent);
        }
        return [...byKey.values()];
      })();
      // Get address information from keyInfo
      const streetNumber = get(trade, "keyInfo.streetNumber") || "";
      const streetName = get(trade, "keyInfo.streetName") || "";
      const unit = get(trade, "keyInfo.unit") || "";
      const city = get(trade, "keyInfo.city") || "";
      const province = get(trade, "keyInfo.province") || "";
      const postalCode = get(trade, "keyInfo.postalCode") || "";
      // Format address for display
      const addressLine1 =
        streetNumber && streetName ? `${streetNumber} ${streetName}` : "";
      const addressLine2 = unit || "";
      const addressLine3 = city && province ? `${city}, ${province}` : "";
      const addressLine4 = postalCode || "";
      const printContent = `

     <!DOCTYPE html>
<html>
<head>
<title>Trade Record Sheet - ${trade.tradeNumber}</title>
<style>
/* A4 Optimized Styling - 210mm x 297mm */
* { box-sizing: border-box; margin: 0; padding: 0; }

body {
  font-family: Arial, sans-serif;
  margin: 0;
  padding: 15mm 12mm; /* A4 margins: top/bottom 15mm, left/right 12mm */
  font-size: 10px;
  line-height: 1.4;
  width: 210mm; /* A4 width */
  background: white;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1px;
  width: 100%;
}

.logo { width: 70px; height: auto; }

.title {
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  flex-grow: 1;
  padding: 0 10px;
}

.date-address {
  text-align: right;
  font-size: 9px;
  line-height: 1.3;
}

.date { font-weight: bold; margin-bottom: 4px; }

.address { line-height: 1.3; }

.section {
  margin-bottom: 12px;
  page-break-inside: avoid; /* Prevent sections from breaking across pages */
}

.section.section-allow-break {
  page-break-inside: auto; /* Allow Financial and following to flow into gap and next page */
}

.section-title {
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 6px;
  border-bottom: 1px solid #333;
  padding-bottom: 10px;
}

.property-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  margin-bottom: 8px;
}

.property-item {
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  line-height: 1.3;
}

.property-label { font-weight: bold; margin-right: 5px; }

table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 8px;
  font-size: 8px;
  page-break-inside: auto;
}

tr { page-break-inside: avoid; page-break-after: auto; }

th, td {
  border: 1px solid #ccc;
  padding: 6px 6px;
  text-align: left;
  line-height: 1.2;
}

th {
  background-color: #f5f5f5;
  font-weight: bold;
  font-size: 8px;
}

.financial-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 10px;
}

.financial-item { text-align: center; }

.financial-label {
  font-weight: bold;
  margin-bottom: 3px;
  font-size: 9px;
}

.financial-value { font-size: 12px; font-weight: bold; }

/* Page break helpers */
.page-break { page-break-after: always; }
.no-break { page-break-inside: avoid; }

@media print {
  body {
    margin: 0;
    padding: 15mm 12mm;
  }

  .no-print { display: none !important; }
  button { display: none !important; }
}
</style>
</head>

<body>

<div class="header">

  <div style="display: flex; flex-direction: column; align-items: flex-start;">
    <img src="/logo3.jpg" alt="Logo" class="logo">

    <div style="font-size: 9px; line-height: 1.2; margin-top: 5px;">
      ABC Realty Inc., Brokerage<br>
      634 Champlain St Dieppe <br>
      NB E1A 1P4<br>
      Phone: 123 456 7890
    </div>
  </div>

  <div class="title">Trade Record Sheet</div>

  <div class="date-address">
    <div class="date">${currentDate}</div>

    <div class="address">
      ${addressLine1}<br>
      ${addressLine2}<br>
      ${addressLine3}<br>
      ${addressLine4}
    </div>

    <div style="font-size: 10px; font-weight: bold; margin-top: 5px;">
      Trade Number - ${
        get(trade, "keyInfo.tradeNumber") || trade.tradeNumber || "-"
      }
    </div>
  </div>

</div>

<div class="section">

  <div class="section-title">Property Name</div>

  <div class="property-grid">

    <div class="property-item">
      <span class="property-label">Type:</span>
      <span>${get(trade, "keyInfo.propertyType")}</span>
    </div>

    <div class="property-item">
      <span class="property-label">Classification:</span>
      <span>${get(trade, "keyInfo.classification")}</span>
    </div>

    <div class="property-item">
      <span class="property-label">Status:</span>
      <span>${get(trade, "keyInfo.status")}</span>
    </div>

    <div class="property-item">
      <span class="property-label">MLS Number:</span>
      <span>${get(trade, "keyInfo.mlsNumber")}</span>
    </div>

    <div class="property-item">
      <span class="property-label">Offer Date:</span>
      <span>${get(trade, "keyInfo.offerDate") ? formatDate(get(trade, "keyInfo.offerDate")) : ""}</span>
    </div>

    <div class="property-item">
      <span class="property-label">Entry Date:</span>
      <span>${get(trade, "keyInfo.entryDate") ? formatDate(get(trade, "keyInfo.entryDate")) : ""}</span>
    </div>

    <div class="property-item">
      <span class="property-label">Firm Date:</span>
      <span>${get(trade, "keyInfo.firmDate") ? formatDate(get(trade, "keyInfo.firmDate")) : ""}</span>
    </div>

    <div class="property-item">
      <span class="property-label">Close Date:</span>
      <span>${get(trade, "keyInfo.closeDate") ? formatDate(get(trade, "keyInfo.closeDate")) : ""}</span>
    </div>

  </div>

</div>

<div class="section">

  <div class="section-title">Conditions</div>

  ${
    trade.conditions && trade.conditions.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Condition</th>
        <th>Due Date</th>
        <th>Condition Met Date</th>
      </tr>
    </thead>

    <tbody>
      ${trade.conditions
        .map(
          (condition) => `

        <tr>
          <td>${condition.conditionText || "-"}</td>
          <td>${
            condition.dueDate ? formatDate(condition.dueDate) : "-"
          }</td>
          <td>${
            condition.conditionMetDate
              ? formatDate(condition.conditionMetDate)
              : "Not met"
          }</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>
          `
      : "<p>No conditions available.</p>"
  }
        </div>





      <div class="section">

  <div class="section-title">Contacts</div>

  ${
    trade.people && trade.people.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Type</th>
        <th>First Name</th>
        <th>Last Name</th>
        <th>Primary Phone</th>
        <th>Secondary Phone</th>
        <th>Email</th>
        <th>Company Name</th>
      </tr>
    </thead>

    <tbody>
      ${trade.people
        .map(
          (person) => `

        <tr>
          <td>${person.type || "-"}</td>
          <td>${person.firstName || "-"}</td>
          <td>${person.lastName || "-"}</td>
          <td>${person.primaryPhone || "-"}</td>
          <td>${person.secondaryPhone || "-"}</td>
          <td>${person.email || "-"}</td>
          <td>${person.companyName || "-"}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No contacts available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Outside Brokers</div>

  ${
    allOutsideBrokersForPrint && allOutsideBrokersForPrint.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Type</th>
        <th>End</th>
        <th>First Name</th>
        <th>Last Name</th>
        <th>Company</th>
        <th>Phone Number</th>
      </tr>
    </thead>

    <tbody>
      ${allOutsideBrokersForPrint
        .map(
          (broker) => `

        <tr>
          <td>${broker.type || "-"}</td>
          <td>${broker.end || "-"}</td>
          <td>${broker.firstName || "-"}</td>
          <td>${broker.lastName || "-"}</td>
          <td>${broker.company || "-"}</td>
          <td>${broker.primaryPhone || "-"}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No outside brokers available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Outside Brokers Commission Details</div>

  ${
    formattedOutsideBrokers.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Agent Name</th>
        <th>Brokerage</th>
        <th>End</th>
        <th>Percentage</th>
        <th>Selling Amount</th>
        <th>Tax</th>
        <th>Total</th>
      </tr>
    </thead>

    <tbody>
      ${formattedOutsideBrokers
        .map(
          (broker) => `

        <tr>
          <td>${broker.agentName}</td>
          <td>${broker.brokerage}</td>
          <td>${broker.end}</td>
          <td>${formatPercentDisplayForSheet(broker.percentage)}</td>
          <td>${formatCurrency(broker.sellingAmount)}</td>
          <td>${formatCurrency(broker.tax)}</td>
          <td>${formatCurrency(broker.total)}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No outside brokers commission details available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Deposit</div>

  ${
    trade.trustRecords && trade.trustRecords.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>We Hold</th>
        <th>Received</th>
        <th>Received From</th>
        <th>Held By</th>
        <th>Deposit Date</th>
        <th>Reference</th>
        <th>Amount</th>
        <th>Payment Type</th>
      </tr>
    </thead>

    <tbody>
      ${trade.trustRecords
        .map(
          (record) => `

        <tr>
          <td>${record.weHold || "-"}</td>
          <td>${record.received || "-"}</td>
          <td>${record.receivedFrom || "-"}</td>
          <td>${record.heldBy || "-"}</td>
          <td>${
            record.depositDate || record.date
              ? formatDate(record.depositDate || record.date)
              : "-"
          }</td>
          <td>${record.reference || "-"}</td>
          <td>${formatCurrency(record.amount || 0)}</td>
          <td>${record.paymentType || "-"}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No deposit records available.</p>"
  }

</div>

<div class="section section-allow-break">

  <div class="section-title">Financial</div>

  <div style="font-weight:bold; font-size:12px; margin-bottom:4px;">Financial:</div>

  <div style="margin-bottom:4px; font-size:10px;">
    <div>Selling Price: <span style="font-weight:bold;">${formatCurrency(
      commissionIncome.sellPrice || trade.keyInfo?.sellPrice || 0,
    )}</span></div>
  </div>

  <div style="margin-bottom:4px; font-size:10px;">
    <div>Listing Commission: <span style="font-weight:bold;">${parseFloat(
      trade.keyInfo?.listCommission || 0,
    )}%</span></div>
  </div>

  <div style="margin-bottom:4px; font-size:10px;">
    <div>Selling Commission: <span style="font-weight:bold;">${parseFloat(
      trade.keyInfo?.sellCommission || 0,
    )}%</span></div>
  </div>

  <div style="display:flex; font-weight:bold; border-bottom:1px solid #ccc; padding:10px 0; border-top:1px solid #ccc; font-size:9px;">
    <div style="width:25%"></div>
    <div style="width:12%; text-align:center;">Listing</div>
    <div style="width:12%; text-align:center;">Selling</div>
    <div style="width:12%; text-align:center;">Sub-Total</div>
    <div style="width:12%; text-align:center;">HST</div>
    <div style="width:12%; text-align:center;">Total</div>
  </div>

  <div style="font-weight:bold; margin-top:6px; margin-bottom:0; font-size:10px;">Income</div>

  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">Commission</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      listingAmount,
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      sellingAmount,
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(subTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(hst)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(total)}</div>
  </div>
          ${
            isCoOperatingSide && referralForExpenses.length === 0
              ? showBaseOfficeAndNetToOffice
                ? `
          <div style="display:flex; padding:3px 0; font-size:9px;">
            <div style="width:25%">Base Office Commission</div>
            <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionListing)}</div>
            <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSelling)}</div>
            <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSubTotal)}</div>
            <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionHST)}</div>
            <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionTotal)}</div>
          </div>
          `
                : ""
              : isCoOperatingSide && referralForExpenses.length > 0
                ? showBaseOfficeAndNetToOffice
                  ? `
<div style="font-weight:bold; margin-top:6px; font-size:10px;">Expenses</div>

${expensesRowsForPrint
  .filter((row) => (row.name || "").includes("Referral"))
  .map(
    (row) => `
<div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
  <div style="width:25%">Ref. ${(row.name || "").replace(/\s*-\s*Referral\s*$/i, "")}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.listing)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.selling)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.subTotal)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.tax)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.total)}</div>
</div>
`,
  )
  .join("")}

<div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
  <div style="width:25%">Base Office Commission</div>
  <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionListing)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSelling)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSubTotal)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionHST)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionTotal)}</div>
</div>

`
                  : ""
                : `
<div style="font-weight:bold; margin-top:6px; font-size:10px;">Expenses</div>

${expensesRowsForPrint
  .map(
    (row) => `
<div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
  <div style="width:25%">${row.name}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.listing)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.selling)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.subTotal)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.tax)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.total)}</div>
</div>
`,
  )
  .join("")}

<div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
  <div style="width:25%">Base Office Commission</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    baseOfficeCommissionListing,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    baseOfficeCommissionSelling,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    baseOfficeCommissionSubTotal,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    baseOfficeCommissionHST,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    baseOfficeCommissionTotal,
  )}</div>
</div>

`
          }

<div style="font-weight:bold; margin-top:6px; font-size:10px;">Agents</div>

${agentDetailsForPrint
  .map(
    (agent) => `
<div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
  <div style="width:25%">(${agent.agentId || ""}) ${agent.agentName || ""}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    agent.agentListing || "0.00",
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    agent.agentSelling || "0.00",
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    agent.agentSubTotal || "0.00",
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    agent.agentHST || "0.00",
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    agent.agentTotal || "0.00",
  )}</div>
</div>
${(agent.officeExpenseFinancialRows || [])
  .map(
    (row) => `
<div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
  <div style="width:25%; padding-left:12px;">${row.label}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.listing)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.selling)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.subTotal)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.hst)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(row.total)}</div>
</div>`,
  )
  .join("")}
`,
  )
  .join("")}

<!-- Liabilities Section -->

${
  totalBuyerRebate > 0
    ? `

<div style="font-weight:bold; margin-top:6px; font-size:10px;">Liabilities</div>

<div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
  <div style="width:25%">Buyer Rebate</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    buyerRebateListing,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    buyerRebateSelling,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    (parseFloat(buyerRebateListing) + parseFloat(buyerRebateSelling)).toFixed(
      2,
    ),
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency("0.00")}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    (parseFloat(buyerRebateListing) + parseFloat(buyerRebateSelling)).toFixed(
      2,
    ),
  )}</div>
</div>

`
    : ""
}

<div style="font-weight:bold; margin-top:6px; font-size:10px;">Net to Office</div>

<div style="display:flex; border-bottom:3px double #000; padding:10px 0; font-size:9px;">
  <div style="width:25%"></div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    netToOfficeListing,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    netToOfficeSelling,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    netToOfficeSubTotal,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    netToOfficeHST,
  )}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(
    netToOfficeTotal,
  )}</div>
</div>

</div>

${
  tradeClassification !== "AGENT DOUBLE ENDER"
    ? `

<div class="section section-allow-break">

  <div class="section-title">Agent Details</div>

  ${
    agentDetailsForPrint && agentDetailsForPrint.length > 0
      ? `

  <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">

    <thead>
      <tr style="background-color:#f2f2f2;">
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Name</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Number</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Percentage</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Lead</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Payment Plan</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Base</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Plan Amount</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Office Expenses</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Buyer Rebate</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Total</th>
      </tr>
    </thead>

    <tbody>
      ${agentDetailsForPrint
        .map(
          (agent) => `

        <tr>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentName || "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentId || "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${formatPercentDisplayForSheet(
            agent.percentage,
          )}</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.lead != null && agent.lead !== "" ? agent.lead : "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            getPaymentPlanDisplayName(agent.feeInfo) || "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            parseFloat(agent.amount || 0),
          )}</td>
          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.feesDeducted || 0,
          )}</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE) > 0
              ? formatCurrency(
                  officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE),
                )
              : "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.buyerRebateIncluded === "yes" && agent.buyerRebateAmount
              ? formatCurrency(agent.buyerRebateAmount)
              : "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.netCommission != null &&
              String(agent.netCommission).trim() !== ""
              ? parseFloat(agent.netCommission)
              : parseFloat(agent.amount || 0) +
                  parseFloat(agent.tax || 0) -
                  parseFloat(
                    agent.totalFees != null && agent.totalFees !== ""
                      ? agent.totalFees
                      : agent.feesDeducted || 0,
                  ) -
                  (agent.buyerRebateIncluded === "yes" &&
                  agent.buyerRebateAmount
                    ? parseFloat(agent.buyerRebateAmount || 0)
                    : 0),
          )}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No agent payment details available.</p>"
  }

</div>

`
    : `

<div class="section section-allow-break">

  <div class="section-title">Agent Details</div>

  ${
    agentDetailsForPrint && agentDetailsForPrint.length > 0
      ? `

  <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">

    <thead>
      <tr style="background-color:#f2f2f2;">
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Name</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Number</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Percentage</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Lead</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Payment Plan</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Base</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Plan Amount</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Office Expenses</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Buyer Rebate</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Total</th>
      </tr>
    </thead>

    <tbody>
      ${agentDetailsForPrint
        .filter((agent) => agent.classification === "AGENT DOUBLE ENDER")
        .map(
          (agent) => `

        <tr>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentName || "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentId || "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${formatPercentDisplayForSheet(
            agent.percentage,
          )}</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.lead != null && agent.lead !== "" ? agent.lead : "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            getPaymentPlanDisplayName(agent.feeInfo) || "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            parseFloat(agent.amount || 0),
          )}</td>
          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.feesDeducted || 0,
          )}</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE) > 0
              ? formatCurrency(
                  officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE),
                )
              : "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.buyerRebateIncluded === "yes" && agent.buyerRebateAmount
              ? formatCurrency(agent.buyerRebateAmount)
              : "-"
          }</td>
          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.netCommission != null &&
              String(agent.netCommission).trim() !== ""
              ? parseFloat(agent.netCommission)
              : parseFloat(agent.amount || 0) +
                  parseFloat(agent.tax || 0) -
                  parseFloat(
                    agent.totalFees != null && agent.totalFees !== ""
                      ? agent.totalFees
                      : agent.feesDeducted || 0,
                  ) -
                  (agent.buyerRebateIncluded === "yes" &&
                  agent.buyerRebateAmount
                    ? parseFloat(agent.buyerRebateAmount || 0)
                    : 0),
          )}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No agent payment details available.</p>"
  }

</div>

`
}

<br/><br/><br/><br/><br/><br/><br/>

<div class="section section-allow-break">

  <div class="section-title">Trust Activity</div>

  ${
    trade.trustRecords && trade.trustRecords.length > 0
      ? `<table><thead><tr>
          <th>Received From</th><th>Deposit Date</th><th>Reference</th><th>Amount</th>
        </tr></thead><tbody>
        ${trade.trustRecords
          .map(
            (record) => `<tr>
              <td>${record.receivedFrom || "-"}</td>
              <td>${
            record.depositDate || record.date
              ? formatDate(record.depositDate || record.date)
              : "-"
          }</td>
              <td>${record.reference || "-"}</td>
              <td>${formatCurrency(record.amount || 0)}</td>
            </tr>`,
          )
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No trust records available.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Real Estate Trust Payments</div>

  ${
    realEstateTrustEFTs.length > 0
      ? `<table><thead><tr>
          <th>EFT #</th><th>Date</th><th>Amount</th><th>Recipient</th><th>Type</th><th>Description</th>
        </tr></thead><tbody>
        ${realEstateTrustEFTs
          .map(
            (eft) => `<tr>
              <td>${eft.eftNumber || ""}</td>
              <td>${
                eft.chequeDate || eft.date
                  ? formatDate(eft.chequeDate || eft.date)
                  : "-"
              }</td>
              <td>${formatCurrency(eft.amount || "")}</td>
              <td>${eft.recipient || ""}</td>
              <td>${getEFTTypeDisplayName(eft.type) || ""}</td>
              <td>${eft.description || ""}</td>
            </tr>`,
          )
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No Real Estate Trust Payments found for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Commission Trust Activity</div>
          ${(() => {
            // For Listing Side, include CommissionTransfer from Real Estate Trust Payments as first transaction

            // For Co-Operating Side, include CommissionTransfer with "Commission Received" description as first transaction

            const commissionTrustEFTsForActivity =
              filterCommissionTrustActivityEFTs(commissionTrustEFTs);

            let displayCommissionTrustEFTs = [
              ...commissionTrustEFTsForActivity,
            ];

            // Normalize classification for comparison (case-insensitive)

            const normalizedClassification = (tradeClassification || "")

              .toUpperCase()

              .trim();

            if (
              normalizedClassification === "LISTING SIDE" ||
              normalizedClassification === "AGENT DOUBLE ENDER"
            ) {
              const commissionTransferEFT = realEstateTrustEFTs.find(
                (eft) => eft.type === "CommissionTransfer",
              );

              console.log(
                "Commission Transfer EFT found:",

                commissionTransferEFT,

                "for classification:",

                normalizedClassification,

                "realEstateTrustEFTs:",

                realEstateTrustEFTs,
              );

              if (commissionTransferEFT) {
                displayCommissionTrustEFTs = [
                  commissionTransferEFT,

                  ...commissionTrustEFTsForActivity,
                ];
              }

              // Check if trust amount is less than total commission and add "Received From" transaction as second item

              const trustRecords = trade.trustRecords || [];

              const commissionIncome =
                trade.commission?.commissionIncomeRows?.[0] || {};

              const listingAmount = commissionIncome.listingAmount
                ? parseFloat(commissionIncome.listingAmount)
                : 0;

              const sellingAmount = commissionIncome.sellingAmount
                ? parseFloat(commissionIncome.sellingAmount)
                : 0;

              const taxRateForTrust =
                trade.customTaxRate != null
                  ? trade.customTaxRate
                  : DEFAULT_TAX_RATE;
              const listingTax = listingAmount * taxRateForTrust;

              const sellingTax = sellingAmount * taxRateForTrust;

              const totalCommission =
                listingAmount + sellingAmount + listingTax + sellingTax;

              // Calculate total trust amount

              const trustAmount = trustRecords.reduce((sum, record) => {
                const amount = parseFloat(
                  record.amount || record.depositAmount || record.total || 0,
                );

                return sum + amount;
              }, 0);

              // If trust amount is less than total commission, find and add "Received From" transaction

              if (trustAmount < totalCommission) {
                // First, try to find the "Received From" transaction from ledger entries (account 10004 with "Received From")

                // Exclude the CommissionTransfer entry by checking if it matches the CommissionTransfer EFT number

                let receivedFromLedger = null;

                if (ledgerEntries && ledgerEntries.length > 0) {
                  // Get the CommissionTransfer EFT number to exclude it

                  const commissionTransferEFTNumber =
                    commissionTransferEFT?.eftNumber?.toString() || "";

                  const candidateEntries = ledgerEntries.filter(
                    (entry) =>
                      entry.accountNumber === "10004" &&
                      entry.debit > 0 &&
                      entry.description &&
                      (entry.description.includes("Received From:") ||
                        entry.description.includes("Received from:")) &&
                      // Exclude if it matches the CommissionTransfer EFT number

                      entry.eftNumber?.toString() !==
                        commissionTransferEFTNumber &&
                      // Exclude entries that have "Commission Trust" in description (those are from CommissionTransfer)

                      !entry.description.includes("Commission Trust") &&
                      // Exclude entries that have "Commission Trust" anywhere (to be safe)

                      !entry.description

                        .toLowerCase()

                        .includes("commission trust") &&
                      !isReversalLedgerEftRef(entry.eftNumber),
                  );

                  // If we have candidates, prefer the one that matches the pattern from final transactions

                  // (format: "Trade #: X - Received From: Name" or "Trade #: X - Address - Received From: Name")

                  if (candidateEntries.length > 0) {
                    // Find the one that matches the final transaction pattern (has " - Received From:" with dash before it)

                    receivedFromLedger =
                      candidateEntries.find((entry) =>
                        entry.description.includes(" - Received From:"),
                      ) || candidateEntries[0];
                  }
                }

                if (receivedFromLedger) {
                  // Extract recipient from description (format: "Trade #: X - Received From: Recipient - Commission Trust - Address")

                  let recipient = receivedFromLedger.payee || "";

                  if (!recipient && receivedFromLedger.description) {
                    const receivedFromMatch =
                      receivedFromLedger.description.match(
                        /Received From:\s*([^-]+?)(?:\s*-|$)/i,
                      );

                    if (receivedFromMatch) {
                      recipient = receivedFromMatch[1].trim();
                    }
                  }

                  // Create the balance commission payment transaction from ledger entry

                  const balanceCommissionPayment = {
                    eftNumber: resolveCommissionTrustDisplayEftNumber(
                      receivedFromLedger,
                      {
                        eftReversals,
                        realEstateTrustEFTs,
                        commissionTrustEFTs,
                        ledgerEntries,
                      },
                    ),

                    date:
                      receivedFromLedger.date || receivedFromLedger.chequeDate,

                    chequeDate:
                      receivedFromLedger.chequeDate || receivedFromLedger.date,

                    amount:
                      receivedFromLedger.debit || receivedFromLedger.amount,

                    recipient: recipient || "N/A",

                    type: "BalanceCommissionPayment",

                    description: "Balance Amount Received",

                    _id: `balance-${receivedFromLedger._id || Date.now()}`,
                  };

                  // Insert as second transaction (after CommissionTransfer)

                  if (commissionTransferEFT) {
                    displayCommissionTrustEFTs = [
                      commissionTransferEFT,

                      balanceCommissionPayment,

                      ...commissionTrustEFTsForActivity,
                    ];
                  } else {
                    displayCommissionTrustEFTs = [
                      balanceCommissionPayment,

                      ...commissionTrustEFTsForActivity,
                    ];
                  }
                } else {
                  // Fallback: Find the "Received From" transaction from trust records

                  const receivedFromRecord = trustRecords.find(
                    (record) =>
                      record.receivedFrom && record.paymentType === "EFT",
                  );

                  if (receivedFromRecord) {
                    // Extract EFT number from reference or use a default

                    const eftNumber =
                      receivedFromRecord.reference?.replace("EFT#", "") ||
                      receivedFromRecord.eftNumber ||
                      "";

                    // Create the balance commission payment transaction

                    const balanceCommissionPayment = {
                      eftNumber: eftNumber,

                      date:
                        receivedFromRecord.depositDate ||
                        receivedFromRecord.date,

                      chequeDate:
                        receivedFromRecord.depositDate ||
                        receivedFromRecord.date,

                      amount: parseFloat(
                        receivedFromRecord.amount ||
                          receivedFromRecord.depositAmount ||
                          receivedFromRecord.total ||
                          0,
                      ),

                      recipient: receivedFromRecord.receivedFrom || "",

                      type: "BalanceCommissionPayment",

                      description: "Balance Amount Received",

                      _id: `balance-${receivedFromRecord._id || Date.now()}`,
                    };

                    // Insert as second transaction (after CommissionTransfer)

                    if (commissionTransferEFT) {
                      displayCommissionTrustEFTs = [
                        commissionTransferEFT,

                        balanceCommissionPayment,

                        ...commissionTrustEFTsForActivity,
                      ];
                    } else {
                      displayCommissionTrustEFTs = [
                        balanceCommissionPayment,

                        ...commissionTrustEFTsForActivity,
                      ];
                    }
                  }
                }
              }
            } else if (
              tradeClassification === "CO-OPERATING SIDE" ||
              tradeClassification === "Co-Operating Side"
            ) {
              // First, try to find CommissionTransfer in Real Estate Trust Payments

              let commissionReceivedEFT = realEstateTrustEFTs.find(
                (eft) => eft.type === "CommissionTransfer",
              );

              // If not found, look for commission trust ledger phrasing (legacy Received From / Paid To on 10004 credits)

              if (!commissionReceivedEFT) {
                commissionReceivedEFT = commissionTrustEFTsForActivity.find(
                  (eft) =>
                    eft.description &&
                    (/(?:Received From|Received from|Paid To|Paid to):\s*[\s\S]*Commission Trust/i.test(
                      eft.description,
                    ) ||
                      eft.description.includes("Commission Received")),
                );
              }

              // If still not found, check ledger entries from trade finalize (account 10004 with "Received From")

              if (
                !commissionReceivedEFT &&
                ledgerEntries &&
                ledgerEntries.length > 0
              ) {
                const commissionReceivedLedger = ledgerEntries.find(
                  (entry) =>
                    entry.accountNumber === "10004" &&
                    entry.debit > 0 &&
                    entry.description &&
                    (entry.description.includes("Received From") ||
                      entry.description.includes("Received from")) &&
                    !isReversalLedgerEftRef(entry.eftNumber),
                );

                if (commissionReceivedLedger) {
                  // Extract recipient from description (format: "Trade #: X - Address - Received From: Recipient")

                  let recipient = commissionReceivedLedger.payee || "";

                  if (!recipient && commissionReceivedLedger.description) {
                    const receivedFromMatch =
                      commissionReceivedLedger.description.match(
                        /Received From:\s*([^-]+?)(?:\s*-|$)/i,
                      );

                    if (receivedFromMatch) {
                      recipient = receivedFromMatch[1].trim();
                    }
                  }

                  // Create EFT-like object from ledger entry

                  commissionReceivedEFT = {
                    eftNumber: resolveCommissionTrustDisplayEftNumber(
                      commissionReceivedLedger,
                      {
                        eftReversals,
                        realEstateTrustEFTs,
                        commissionTrustEFTs,
                        ledgerEntries,
                      },
                    ),

                    date:
                      commissionReceivedLedger.date ||
                      commissionReceivedLedger.chequeDate,

                    chequeDate:
                      commissionReceivedLedger.chequeDate ||
                      commissionReceivedLedger.date,

                    amount:
                      commissionReceivedLedger.debit ||
                      commissionReceivedLedger.amount,

                    recipient: recipient || "N/A",

                    type: "CommissionTransfer",

                    description: "Commission Received",

                    _id: commissionReceivedLedger._id,
                  };
                }
              }

              // If still not found, check by recipient name (common outside broker names)

              if (!commissionReceivedEFT) {
                commissionReceivedEFT = commissionTrustEFTsForActivity.find(
                  (eft) =>
                    eft.recipient &&
                    (eft.recipient.includes("ABC") ||
                      eft.recipient.includes("Realty Inc")),
                );
              }

              if (commissionReceivedEFT) {
                // Create a formatted transaction with "Commission Received" as description

                const formattedEFT = {
                  ...commissionReceivedEFT,

                  description: "Commission Received",
                };

                // If the transaction was found in commissionTrustEFTs, remove it from the array first

                const remainingEFTs =
                  commissionReceivedEFT._id &&
                  commissionTrustEFTsForActivity.some(
                    (eft) => eft._id === commissionReceivedEFT._id,
                  )
                    ? commissionTrustEFTsForActivity.filter(
                        (eft) => eft._id !== commissionReceivedEFT._id,
                      )
                    : commissionTrustEFTsForActivity;

                displayCommissionTrustEFTs = [formattedEFT, ...remainingEFTs];
              }
            }

            if (
              !showBaseOfficeAndNetToOffice &&
              agentCalculationsFiltered &&
              agentCalculationsFiltered.length > 0
            ) {
              const selectedAgents = agentCalculationsFiltered;
              displayCommissionTrustEFTs = displayCommissionTrustEFTs.filter(
                (eft) => {
                  if (eft.type !== "AgentCommissionTransfer") return false;
                  return selectedAgents.some((selectedAgent) => {
                    const selectedAgentId =
                      selectedAgent?.agentId != null
                        ? selectedAgent.agentId.toString()
                        : "";
                    const selectedAgentName = (selectedAgent?.agentName || "")
                      .trim()
                      .toLowerCase();

                    const matchId =
                      selectedAgentId &&
                      eft.agentId != null &&
                      eft.agentId.toString() === selectedAgentId;
                    const matchName =
                      selectedAgentName &&
                      eft.agentName &&
                      (eft.agentName || "").trim().toLowerCase() ===
                        selectedAgentName;
                    const matchRecipient =
                      selectedAgentName &&
                      eft.recipient &&
                      (eft.recipient || "")
                        .trim()
                        .toLowerCase()
                        .includes(selectedAgentName);
                    return matchId || matchName || matchRecipient;
                  });
                },
              );
            }

            displayCommissionTrustEFTs = filterCommissionTrustActivityEFTs(
              displayCommissionTrustEFTs,
            );

            return displayCommissionTrustEFTs.length > 0
              ? `<table><thead><tr>
<th>EFT #</th><th>Date</th><th>Amount</th><th>Recipient</th><th>Type</th><th>Description</th>
</tr></thead><tbody>
${displayCommissionTrustEFTs
  .map(
    (eft) => `<tr>
      <td>${formatCommissionTrustActivityEftNumber(eft, {
        eftReversals,
        realEstateTrustEFTs,
        commissionTrustEFTs,
        ledgerEntries,
      })}</td>
      <td>${
        eft.chequeDate || eft.date
          ? formatDate(eft.chequeDate || eft.date)
          : "-"
      }</td>
      <td>${formatCurrency(eft.amount || "")}</td>
      <td>${eft.recipient || ""}</td>
      <td>${getEFTTypeDisplayName(eft.type) || ""}</td>
      <td>${eft.description || ""}</td>
    </tr>`,
  )
  .join("")}
</tbody></table>`
              : `<div class="text-gray-500">No Commission Trust Activity found for this trade.</div>`;
          })()}

</div>

<div class="section">

  <div class="section-title">Commission Trust Agreement</div>

  <div style="margin-bottom: 15px; font-size: 10px; line-height: 1.4;">
    It is understood between all parties that this agreement shall constitute a Commission Trust Agreement as set out in the contract.
  </div>

  <div style="margin-bottom: 15px; font-size: 10px; line-height: 1.4;">
    To the best of my knowledge the above information is correct. Dated at Markham on ${currentDate}
  </div>

  ${(agentsForTrustAgreement && agentsForTrustAgreement.length > 0
    ? agentsForTrustAgreement
    : []
  )
    .map(
      (agent) => `
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
    <div style="font-size: 10px;">
      ${agent.agentName || "Agent Name"}, ${agent.agentId ?? "Agent Number"}
    </div>
    <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
  </div>
`,
    )
    .join("")}
  ${
    !agentsForTrustAgreement || agentsForTrustAgreement.length === 0
      ? `
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
    <div style="font-size: 10px;">Agent Name, Agent Number</div>
    <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
  </div>
`
      : ""
  }

  <div style="display: flex; justify-content: space-between; align-items: center;">
    <div style="font-size: 10px;">
      Broker of Record: ABC Realty Inc., Brokerage
    </div>

    <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
  </div>

</div>

${(() => {
  // Calculate YTD Income Details for the agent - only show if trade is finalized
  if (!trade.isFinalized) return "";

  const agentsForYTD =
    agentDetailsForPrint && agentDetailsForPrint.length > 0
      ? agentDetailsForPrint
      : (agentCalculations || []).filter(
          (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
        );
  if (agentYTDEfts.length > 0 && agentsForYTD.length > 0) {
    const agentId = agentsForYTD[0].agentId;
    const agentName = agentsForYTD[0].agentName;

    // Calculate totals from YTD EFTs
    const totalNetCommission = agentYTDEfts.reduce(
      (sum, eft) => sum + parseFloat(eft.amount || 0),
      0,
    );

    // Calculate fees and buyer rebate from all trades associated with YTD EFTs
    let totalFees = 0;
    let totalBuyerRebate = 0;
    let totalHST = 0;

    // Sum fees, buyer rebates, and HST from each trade's agent commission list
    agentYTDEfts.forEach((eft) => {
      const eftTrade = eft.tradeData || eft.tradeId; // Use attached tradeData or fallback to tradeId
      const netCommission = parseFloat(eft.amount || 0);

      // HST portion of tax-inclusive net commission (same as Agent Summary)
      const hst = netCommission * INCLUSIVE_HST_FRACTION;
      totalHST += hst;

      if (eftTrade && eftTrade.agentCommissionList) {
        // Find agent commission data - try multiple matching strategies
        let tradeAgent = eftTrade.agentCommissionList.find(
          (ac) => ac.agentId === agentId || ac.agentName === agentName,
        );

        if (!tradeAgent && agentInfoForYTD?.employeeNo) {
          tradeAgent = eftTrade.agentCommissionList.find(
            (ac) => ac.agentId === agentInfoForYTD.employeeNo.toString(),
          );
        }

        if (!tradeAgent && eftTrade.agentCommissionList.length === 1) {
          tradeAgent = eftTrade.agentCommissionList[0];
        }

        if (tradeAgent) {
          // Use totalFees if available, otherwise calculate fees based on agent fee plan
          let feesDeducted = parseFloat(
            tradeAgent.totalFees || tradeAgent.feesDeducted || 0,
          );

          // If fees not found, calculate based on agent fee plan
          if (!feesDeducted && agentInfoForYTD?.feeInfo) {
            const feePlan = agentInfoForYTD.feeInfo;
            let baseFee = 0;

            switch (feePlan) {
              case "plan250":
                baseFee = 250;
                break;
              case "plan500":
                baseFee = 500;
                break;
              case "plan9010":
                baseFee = netCommission * 0.1;
                break;
              case "plan955":
                baseFee = netCommission * 0.05;
                break;
              case "plan8515":
                baseFee = netCommission * 0.15;
                break;
              case "plan5050":
                baseFee = netCommission * 0.5;
                break;
              case "plan8020":
                baseFee = netCommission * 0.2;
                break;
              case "plan150":
                baseFee = 150;
                break;
              default:
                baseFee = 0;
            }

            const taxRateYTD =
              eftTrade?.customTaxRate != null
                ? eftTrade.customTaxRate
                : DEFAULT_TAX_RATE;
            const taxOnFees = baseFee * taxRateYTD;
            feesDeducted = baseFee + taxOnFees;
          }

          totalFees += feesDeducted;

          // Get buyer rebate if included
          if (
            tradeAgent.buyerRebateIncluded === "yes" &&
            tradeAgent.buyerRebateAmount
          ) {
            totalBuyerRebate += parseFloat(tradeAgent.buyerRebateAmount || 0);
          }
        }
      }
    });

    const netCommissionWithoutHST = totalNetCommission - totalHST;
    const grossEarnings = totalNetCommission + totalFees;
    const totalIncludingHST = totalNetCommission;

    return `
      <div class="section">

        <div class="section-title">YTD Income DETAILS</div>

        <div style="margin-top: 15px; font-size: 11px; line-height: 1.6;">

          <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
            <span style="font-weight: bold;">GROSS Earnings:</span>
            <span>${formatCurrency(grossEarnings)}</span>
          </div>

          <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
            <span style="font-weight: bold;">TOTAL OFFICE SHARE:</span>
            <span>${formatCurrency(totalFees)}</span>
          </div>

          <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
            <span style="font-weight: bold;">NET Commission:</span>
            <span>${formatCurrency(netCommissionWithoutHST)}</span>
          </div>

          <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
            <span style="font-weight: bold;">HST PAID:</span>
            <span>${formatCurrency(totalHST)}</span>
          </div>

          <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
            <span style="font-weight: bold;">TOTAL INCLUDING HST:</span>
            <span>${formatCurrency(totalIncludingHST)}</span>
          </div>

          <div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
            <span style="font-weight: bold;">BUYER REBATE:</span>
            <span>${formatCurrency(-totalBuyerRebate)}</span>
          </div>

        </div>

      </div>
    `;
  }

  return "";
})()}

</body>
</html>

`;
      // Write content to iframe
      iframeDoc.open();
      iframeDoc.write(printContent);
      iframeDoc.close();
      // Wait for content to load
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // Generate PDF using our utility
      console.log("?? Converting HTML to PDF...");
      const pdfBlob = await generatePDFFromElement(
        iframeDoc.body,
        `Trade_${trade.tradeNumber}.pdf`,
      );
      console.log(
        "? PDF generated, size:",
        Math.round(pdfBlob.size / 1024),
        "KB",
      );
      // Clean up iframe
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      if (pdfBlob.size < 500) {
        throw new Error("PDF generated but appears to be empty");
      }
      // Convert PDF to base64
      const base64 = await blobToBase64(pdfBlob);
      const pdfBase64 = `data:application/pdf;base64,${base64}`;
      const fileName = `Trade_${trade.tradeNumber}_${Date.now()}.pdf`;
      console.log("?? Uploading PDF to Dropbox...");
      let currentAccessToken = dropboxAccessToken;
      // Try to upload with current token
      try {
        const response = await axiosInstance.post(
          "/api/dropbox/upload-pdf-to-dropbox",
          {
            pdfBase64: pdfBase64,
            fileName: fileName,
            accessToken: currentAccessToken,
          },
        );
        if (response.data.success) {
          alert(
            `? SUCCESS!\n\nPDF uploaded to Dropbox!\n\nFile: ${
              response.data.file.name
            }\nSize: ${Math.round(pdfBlob.size / 1024)} KB\nPath: ${
              response.data.file.path
            }`,
          );
          setIsUploadingToDropbox(false);
          return;
        }
      } catch (uploadError) {
        // Check if token expired
        if (
          uploadError.response?.status === 401 ||
          uploadError.response?.data?.needsTokenRefresh
        ) {
          console.log("?? Token expired, refreshing...");
          const refreshToken = localStorage.getItem("dropboxRefreshToken");
          if (refreshToken) {
            try {
              const refreshResponse = await axiosInstance.post(
                "/api/dropbox/refresh-token",
                {
                  refreshToken: refreshToken,
                },
              );
              if (refreshResponse.data.success) {
                const newAccessToken = refreshResponse.data.accessToken;
                localStorage.setItem("dropboxAccessToken", newAccessToken);
                setDropboxAccessToken(newAccessToken);
                console.log("? Token refreshed, retrying upload...");
                // Retry with new token
                const retryResponse = await axiosInstance.post(
                  "/api/dropbox/upload-pdf-to-dropbox",
                  {
                    pdfBase64: pdfBase64,
                    fileName: fileName,
                    accessToken: newAccessToken,
                  },
                );
                if (retryResponse.data.success) {
                  alert(
                    `? SUCCESS!\n\nPDF uploaded to Dropbox!\n\nFile: ${
                      retryResponse.data.file.name
                    }\nSize: ${Math.round(pdfBlob.size / 1024)} KB\nPath: ${
                      retryResponse.data.file.path
                    }`,
                  );
                  setIsUploadingToDropbox(false);
                  return;
                }
              }
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
              localStorage.removeItem("dropboxAccessToken");
              localStorage.removeItem("dropboxRefreshToken");
              setDropboxAccessToken(null);
              setDropboxRefreshToken(null);
              alert(
                '? Session expired. Please click "Send to Dropbox" to re-authenticate.',
              );
              setIsUploadingToDropbox(false);
              return;
            }
          } else {
            localStorage.removeItem("dropboxAccessToken");
            setDropboxAccessToken(null);
            alert(
              '? Session expired. Please click "Send to Dropbox" again to re-authenticate.',
            );
            setIsUploadingToDropbox(false);
            return;
          }
        }
        // If not 401, show the error
        console.error("Upload error:", uploadError);
        alert(
          `? Upload failed: ${
            uploadError.response?.data?.message || uploadError.message
          }`,
        );
        setIsUploadingToDropbox(false);
      }
    } catch (error) {
      console.error("PDF generation error:", error);
      alert(`? Error: ${error.message || "Failed to generate PDF"}`);
      setIsUploadingToDropbox(false);
    }
  };
  const getTransactionDetails = () => {
    // You may want to adjust these values to match your backend logic
    const commissionIncome = trade.commission?.commissionIncomeRows?.[0] || {};
    const firstAgent = trade.agentCommissionList?.[0] || {};
    const address = `${get(trade, "keyInfo.streetNumber") || ""} ${
      get(trade, "keyInfo.streetName") || ""
    }`.trim();
    const commissionDescription = `Trade #${trade.tradeNumber} - ${address}`;
    const commissionDate = get(trade, "keyInfo.closeDate")
      ? formatDate(get(trade, "keyInfo.closeDate"))
      : "-";
    const taxRateForTxn =
      trade.customTaxRate != null && trade.customTaxRate !== ""
        ? Number(trade.customTaxRate) > 1 && Number(trade.customTaxRate) <= 100
          ? Number(trade.customTaxRate) / 100
          : Number(trade.customTaxRate)
        : DEFAULT_TAX_RATE;
    const totalFees = parseFloat(firstAgent.feesDeducted || 0);
    const hstInputTaxCredit =
      parseFloat(commissionIncome.sellingAmount || 0) * taxRateForTxn;
    const agentAmount = parseFloat(firstAgent.amount || 0);
    const agentNetCommission = parseFloat(firstAgent.netCommission || 0);
    const feesDeducted = parseFloat(firstAgent.feesDeducted || 0);
    const totalFeesWithTax = feesDeducted + feesDeducted * taxRateForTxn;
    // Main 8 transactions
    const mainTxns = [
      {
        date: commissionDate,
        accountNumber: "40100",
        accountName: "Commission Income",
        debit: "",
        credit: commissionIncome.listingAmount,
        description: commissionDescription,
      },
      {
        date: commissionDate,
        accountNumber: "23001",
        accountName: "HST Input Tax Credit",
        debit: commissionIncome.sellingAmount * taxRateForTxn,
        credit: "",
        description: commissionDescription,
      },
      {
        date: commissionDate,
        accountNumber: "23000",
        accountName: "HST Collected",
        debit: "",
        credit: commissionIncome.sellingAmount * taxRateForTxn,
        description: commissionDescription,
      },
      {
        date: commissionDate,
        accountNumber: "50100",
        accountName: "Agent's Commission",
        debit: agentAmount,
        credit: "",
        description: commissionDescription,
      },
      {
        date: commissionDate,
        accountNumber: "21500",
        accountName: "Commission Payable",
        debit: "",
        credit: agentNetCommission,
        description: commissionDescription,
      },
      {
        date: commissionDate,
        accountNumber: "44100",
        accountName: "Fee Deducted Income",
        debit: "",
        credit: feesDeducted,
        description: commissionDescription,
      },
      {
        date: commissionDate,
        accountNumber: "23001",
        accountName: "HST Input Tax Credit",
        debit: "",
        credit: (totalFeesWithTax - feesDeducted).toFixed(2),
        description: commissionDescription,
      },
      {
        date: commissionDate,
        accountNumber: "12200",
        accountName: "A/R - Commission From Deals",
        debit: commissionIncome.listingAmount
          ? (
              parseFloat(commissionIncome.listingAmount) + hstInputTaxCredit
            ).toFixed(2)
          : "",
        credit: "",
        description: commissionDescription,
      },
    ];
    // Next 2 transactions (Payment Received)
    const paymentTxns = [
      {
        date: commissionDate,
        accountNumber: "10004",
        accountName: "CASH - COMMISSION TRUST ACCOUNT",
        debit: agentNetCommission,
        credit: "",
        description: `Trade #${trade.tradeNumber}, Paid to: Agent`,
      },
      {
        date: commissionDate,
        accountNumber: "21500",
        accountName: "Commission Payable",
        debit: agentNetCommission,
        credit: "",
        description: `Trade #${trade.tradeNumber}, Paid to: Agent`,
      },
      {
        date: commissionDate,
        accountNumber: "10004",
        accountName: "CASH - COMMISSION TRUST ACCOUNT",
        debit: feesDeducted,
        credit: "",
        description: `Trade #${trade.tradeNumber}, Paid to: ABC Realty Inc., Brokerage`,
      },
      {
        date: commissionDate,
        accountNumber: "10001",
        accountName: "CASH - CURRENT ACCOUNT",
        debit: "",
        credit: feesDeducted,
        description: `Trade #${trade.tradeNumber}, Paid to: ABC Realty Inc., Brokerage`,
      },
    ];
    return { mainTxns, paymentTxns };
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-4xl w-full relative overflow-y-auto max-h-[90vh]">
        <div className="flex gap-3 absolute top-4 right-6 z-10">
          <button
            onClick={() => {
              const agents = (trade?.agentCommissionList || []).filter(
                (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
              );
              if (agents.length > 1) {
                setShowPrintAgentModal(true);
              } else {
                handlePrint(null);
              }
            }}
            disabled={isPrinting}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPrinting ? "Generating..." : "Print"}
          </button>
          {/* Dropbox Connect Button */}
          <button
            onClick={DropboxHandlers.handleDropboxAuth}
            className="bg-indigo-600 text-white px-3 py-2 rounded hover:bg-indigo-700 transition-colors flex items-center gap-2 text-sm"
            title="Connect or reconnect to Dropbox"
          >
            <svg
              className="h-4 w-4"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M17.464 6.56c-.37-.33-.888-.33-1.32 0L12 10.7 7.84 6.56c-.43-.33-.95-.33-1.32 0-.37.33-.37.86 0 1.19l4.72 4.72c.18.18.43.28.68.28s.5-.1.68-.28l4.72-4.72c.37-.33.37-.86.06-1.19zM12 15.5c-2.33 0-4.47-.82-6.14-2.18l-.01.01A8.02 8.02 0 0112 4c3.87 0 7.13 2.75 7.9 6.4.13.64.2 1.3.2 1.98 0 4.42-3.58 8-8 8zm0-18C5.93-2.5.93 2.5.93 8.57c0 2.77 1.02 5.3 2.71 7.24 0 0 0 .01.01.01 1.92 2.21 4.72 3.6 7.85 3.6 5.8 0 10.5-4.7 10.5-10.5C21.93 3.08 17.43-1.42 12-1.42z" />
            </svg>
          </button>
          {/* Dropbox Upload Button */}
          <button
            onClick={handleDropboxUpload}
            disabled={isUploadingToDropbox}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isUploadingToDropbox ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Uploading...
              </>
            ) : (
              <>
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
                Upload to Dropbox
              </>
            )}
          </button>
          {locked ? (
            <span className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded">
              Locked after review
            </span>
          ) : (
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
          >
            Edit
          </button>
          )}
          <button
            className="text-2xl text-gray-500 hover:text-gray-700 px-2"
            onClick={onClose}
            style={{ lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
        {showEditModal ? (
          <TradeEditModal
            trade={trade}
            onClose={() => setShowEditModal(false)}
            onUpdate={onUpdate}
          />
        ) : (
          <>
            <h2 className="text-2xl font-bold mb-6 text-yellow-700 mt-8 flex items-center gap-3">
              Trade Details: #{trade.tradeNumber}
              <WorkflowBadge record={trade} />
            </h2>
            <TrsSignatureActions trade={trade} onUpdate={onUpdate} />
            {/* Address Section */}
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Address</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">Street #</span>
                  <br />
                  {get(trade, "keyInfo.streetNumber")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Street Name</span>
                  <br />
                  {get(trade, "keyInfo.streetName")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Unit</span>
                  <br />
                  {get(trade, "keyInfo.unit")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">City</span>
                  <br />
                  {get(trade, "keyInfo.city")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Province</span>
                  <br />
                  {get(trade, "keyInfo.province")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Postal Code</span>
                  <br />
                  {get(trade, "keyInfo.postalCode")}
                </div>
              </div>
            </div>
            {/* Dates Section */}
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Important Dates</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">Offer Date</span>
                  <br />
                  {get(trade, "keyInfo.offerDate")
                    ? formatDate(get(trade, "keyInfo.offerDate"))
                    : ""}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Firm Date</span>
                  <br />
                  {get(trade, "keyInfo.firmDate")
                    ? formatDate(get(trade, "keyInfo.firmDate"))
                    : ""}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Close Date</span>
                  <br />
                  {get(trade, "keyInfo.closeDate")
                    ? formatDate(get(trade, "keyInfo.closeDate"))
                    : ""}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Entry Date</span>
                  <br />
                  {get(trade, "keyInfo.entryDate")
                    ? formatDate(get(trade, "keyInfo.entryDate"))
                    : ""}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">
                    Finalized Date
                  </span>
                  <br />
                  {get(trade, "keyInfo.finalizedDate")
                    ? formatDate(get(trade, "keyInfo.finalizedDate"))
                    : ""}
                </div>
              </div>
            </div>
            {/* Key Information Section */}
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Key Information</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">
                    Listing Number
                  </span>
                  <br />
                  {get(trade, "keyInfo.listingNumber")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Sell Price</span>
                  <br />
                  {get(trade, "keyInfo.sellPrice")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">
                    Classification
                  </span>
                  <br />
                  {get(trade, "keyInfo.classification")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Firm</span>
                  <br />
                  {get(trade, "keyInfo.firm")}
                </div>
              </div>
            </div>
            {/* Commission */}
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Commission</h3>
              <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">List %</span>
                  <br />
                  {get(trade, "keyInfo.listCommission")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Sell %</span>
                  <br />
                  {get(trade, "keyInfo.sellCommission")}
                </div>
              </div>
            </div>
            {/* Property Details */}
            <div className="mb-4">
              <h3 className="font-semibold text-lg mb-2">Property Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div>
                  <span className="text-gray-500 font-normal">
                    Property Type
                  </span>
                  <br />
                  {get(trade, "keyInfo.propertyType")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">Status</span>
                  <br />
                  {get(trade, "keyInfo.status")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">MLS #</span>
                  <br />
                  {get(trade, "keyInfo.mlsNumber")}
                </div>
                <div>
                  <span className="text-gray-500 font-normal">We Manage</span>
                  <br />
                  {get(trade, "keyInfo.weManage")}
                </div>
              </div>
            </div>
            {/* People Information Section */}
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">People Information</h3>
              {trade.people && trade.people.length > 0 ? (
                <table className="min-w-full border border-gray-300 mb-2 text-sm">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Type
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        First Name
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Last Name
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Primary Phone
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Secondary Phone
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Email
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Company Name
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {trade.people.map((person, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{person.type}</td>
                        <td className="border px-2 py-1">
                          {person.firstName || "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {person.lastName || "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {person.primaryPhone || "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {person.secondaryPhone || "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {person.email || "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {person.companyName || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-gray-500">
                  No people information available.
                </div>
              )}
            </div>
            {/* Outside Brokers Section - includes referral agents from Agent Info (for trade details sheet) */}
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Outside Brokers</h3>
              {(() => {
                const referralAgents = (trade.agentCommissionList || []).filter(
                  (a) => (a.classification || "").toUpperCase() === "REFERRAL",
                );
                const referralAsBrokers = referralAgents.map((a) => ({
                  type: "Referral",
                  firstName: "-",
                  lastName: "-",
                  company: a.agentName || "Referral",
                  end: "Selling End",
                  primaryPhone: "-",
                }));
                const allOutsideBrokers = [
                  ...(trade.outsideBrokers || []),
                  ...referralAsBrokers,
                ];
                return allOutsideBrokers.length > 0 ? (
                  <table className="min-w-full border border-gray-300 mb-2 text-sm">
                    <thead>
                      <tr>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Type
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          End
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          First Name
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Last Name
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Company
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Phone Number
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {allOutsideBrokers.map((broker, idx) => (
                        <tr key={idx}>
                          <td className="border px-2 py-1">
                            {broker.type || "-"}
                          </td>
                          <td className="border px-2 py-1">
                            {broker.end || "-"}
                          </td>
                          <td className="border px-2 py-1">
                            {broker.firstName || "-"}
                          </td>
                          <td className="border px-2 py-1">
                            {broker.lastName || "-"}
                          </td>
                          <td className="border px-2 py-1">
                            {broker.company || "-"}
                          </td>
                          <td className="border px-2 py-1">
                            {broker.primaryPhone || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-gray-500">
                    No outside brokers information available.
                  </div>
                );
              })()}
            </div>
            {/* Outside Brokers Commission Details Section - includes referral agents (for trade details sheet) */}
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">
                Outside Brokers Commission Details
              </h3>
              {(() => {
                const referralAgentsForCommission = (
                  trade.agentCommissionList || []
                ).filter(
                  (a) => (a.classification || "").toUpperCase() === "REFERRAL",
                );
                const referralCommissionRows = referralAgentsForCommission.map(
                  (a) => ({
                    agentName: a.agentName || "",
                    brokerage: a.agentName || "Referral",
                    end: "Selling",
                    percentage: a.percentage,
                    sellingAmount: a.amount || "0",
                    tax: a.tax || "0",
                    total: a.total || "0",
                  }),
                );
                const allCommissionRows = [
                  ...(trade.commission?.outsideBrokersRows || []),
                  ...referralCommissionRows,
                ];
                const getEndValueForPrint = (b) => {
                  const matchingBroker1 = trade.outsideBrokers?.find((ob) => {
                    const outsideBrokerName =
                      `${ob.firstName} ${ob.lastName}`.trim();
                    return (
                      outsideBrokerName === b.agentName ||
                      ob.company === b.brokerage
                    );
                  });
                  if (matchingBroker1) {
                    if (
                      matchingBroker1.end?.toLowerCase().includes("listing") ||
                      matchingBroker1.type?.toLowerCase().includes("listing")
                    ) {
                      return "Listing";
                    }
                  }
                  if (
                    b.end?.toLowerCase().includes("listing") ||
                    b.type?.toLowerCase().includes("listing")
                  ) {
                    return "Listing";
                  }
                  return "Selling";
                };
                return allCommissionRows.length > 0 ? (
                  <table className="min-w-full border border-gray-300 mb-2 text-sm">
                    <thead>
                      <tr>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Agent Name
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Brokerage
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          End
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Percentage
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Selling Amount
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Tax
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {allCommissionRows.map((broker, idx) => (
                        <tr key={idx}>
                          <td className="border px-2 py-1">
                            {broker.agentName || "-"}
                          </td>
                          <td className="border px-2 py-1">
                            {broker.brokerage || "-"}
                          </td>
                          <td className="border px-2 py-1">
                            {getEndValueForPrint(broker)}
                          </td>
                          <td className="border px-2 py-1">
                            {formatPercentDisplayForSheet(broker.percentage)}
                          </td>
                          <td className="border px-2 py-1">
                            {formatCurrency(broker.sellingAmount)}
                          </td>
                          <td className="border px-2 py-1">
                            {formatCurrency(broker.tax)}
                          </td>
                          <td className="border px-2 py-1">
                            {formatCurrency(broker.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-gray-500">
                    No outside brokers commission details available.
                  </div>
                );
              })()}
            </div>
            {/* Trust Records Section */}
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Trust Records</h3>
              {trade.trustRecords && trade.trustRecords.length > 0 ? (
                <table className="min-w-full border border-gray-300 mb-2 text-sm">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        We Hold
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Received
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Received From
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Held By
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Deposit Date
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Reference
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Amount
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Payment Type
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {trade.trustRecords.map((t, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{t.weHold || "-"}</td>
                        <td className="border px-2 py-1">
                          {t.received || "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {t.receivedFrom || "-"}
                        </td>
                        <td className="border px-2 py-1">{t.heldBy || "-"}</td>
                        <td className="border px-2 py-1">
                          {t.depositDate || t.date
                            ? formatDate(t.depositDate || t.date)
                            : "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {t.reference || "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {formatCurrency(t.amount || "-")}
                        </td>
                        <td className="border px-2 py-1">
                          {t.paymentType || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-gray-500">No trust records.</div>
              )}
            </div>
            {/* Conditions Section */}
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Conditions</h3>
              {trade.conditions && trade.conditions.length > 0 ? (
                <table className="min-w-full border border-gray-300 mb-2 text-sm">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Condition
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Due Date
                      </th>
                      <th className="px-2 py-1 text-left bg-gray-100 border">
                        Condition Met Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {trade.conditions.map((condition, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">
                          {condition.conditionText || "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {condition.dueDate
                            ? formatDate(condition.dueDate)
                            : "-"}
                        </td>
                        <td className="border px-2 py-1">
                          {condition.conditionMetDate
                            ? formatDate(condition.conditionMetDate)
                            : "Not met"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-gray-500">No conditions available.</div>
              )}
            </div>
            {/* Commission Section */}
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Commission Details</h3>
              {trade.commission && (
                <>
                  <div className="mb-4">
                    <h4 className="text-gray-700 mb-1">Sale Closing Rows</h4>
                    {renderTable(trade.commission.saleClosingRows)}
                  </div>
                  <div className="mb-4">
                    <h4 className="text-gray-700 mb-1">
                      Commission Income Rows
                    </h4>
                    {renderTable(trade.commission.commissionIncomeRows)}
                  </div>
                  <div className="mb-4">
                    <h4 className="text-gray-700 mb-1">Outside Brokers Rows</h4>
                    {renderTable(trade.commission.outsideBrokersRows)}
                  </div>
                </>
              )}
            </div>
            {/* Agent Commission Info Section - only non-referral agents (referrals show under Outside Brokers only) */}
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">
                Agent Commission Info
              </h3>
              {(() => {
                const agentListForTable = (
                  trade.agentCommissionList || []
                ).filter(
                  (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
                );
                return agentListForTable.length > 0 ? (
                  <table className="min-w-full border border-gray-300 mb-2 text-sm">
                    <thead>
                      <tr>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Agent Name
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Classification
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Amount
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Net Commission
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Lead
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Fee Info
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Total Fees
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Office Expenses
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Tax
                        </th>
                        <th className="px-2 py-1 text-left bg-gray-100 border">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentListForTable.map((a, idx) => (
                        <tr key={idx}>
                          <td className="border px-2 py-1">{a.agentName}</td>
                          <td className="border px-2 py-1">
                            {a.classification}
                          </td>
                          <td className="border px-2 py-1">
                            {formatCurrency(a.amount)}
                          </td>
                          <td className="border px-2 py-1">
                            {formatCurrency(a.netCommission)}
                          </td>
                          <td className="border px-2 py-1">{a.lead}</td>
                          <td className="border px-2 py-1">
                            {getPaymentPlanDisplayName(a.feeInfo)}
                          </td>
                          <td className="border px-2 py-1">
                            {formatCurrency(a.totalFees)}
                          </td>
                          <td className="border px-2 py-1">
                            {parseFloat(a.officeExpensesTotal || 0) > 0
                              ? formatCurrency(a.officeExpensesTotal)
                              : "-"}
                          </td>
                          <td className="border px-2 py-1">
                            {formatCurrency(a.tax)}
                          </td>
                          <td className="border px-2 py-1">
                            {formatCurrency(a.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-gray-500">No agent commission data.</div>
                );
              })()}
            </div>
            {/* EFT Payment Tables Section */}
            <EFTPaymentTables trade={trade} />
            {/* Transaction Details Section */}
          </>
        )}
      </div>
      {showPrintAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              Select agent for Trade Record Sheet
            </h3>
            <div className="space-y-2 mb-4">
              {(() => {
                const nonRefList = (trade?.agentCommissionList || []).filter(
                  (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
                );
                const tradeClassification = (
                  trade?.keyInfo?.classification || ""
                )
                  .toString()
                  .trim()
                  .toUpperCase();
                const isAgentDoubleEnder =
                  tradeClassification === "AGENT DOUBLE ENDER" ||
                  nonRefList.some(
                    (a) =>
                      (a.classification || "").toUpperCase() ===
                      "AGENT DOUBLE ENDER",
                  );

                if (!isAgentDoubleEnder) {
                  return nonRefList.map((agent, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setShowPrintAgentModal(false);
                        handlePrint(idx);
                      }}
                      className="w-full text-left px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition-colors"
                    >
                      {agent.agentName || `Agent ${idx + 1}`}
                    </button>
                  ));
                }

                // Agent Double Ender: each agent appears twice (LISTING + SELLING). Show each agent once.
                const byAgent = new Map();
                nonRefList.forEach((agent, idx) => {
                  const agentIdKey =
                    agent.agentId != null && agent.agentId !== ""
                      ? String(agent.agentId)
                      : null;
                  const agentNameKey = (agent.agentName || "").trim();
                  const key = agentIdKey || agentNameKey || `idx-${idx}`;
                  if (!byAgent.has(key)) {
                    byAgent.set(key, {
                      agentName: agent.agentName || "Agent",
                      indices: [],
                    });
                  }
                  byAgent.get(key).indices.push(idx);
                });

                const agentOptions = Array.from(byAgent.values());
                return agentOptions.map((opt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setShowPrintAgentModal(false);
                      handlePrint(opt.indices);
                    }}
                    className="w-full text-left px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition-colors"
                  >
                    {opt.agentName}
                  </button>
                ));
              })()}

              <button
                type="button"
                onClick={() => {
                  setShowPrintAgentModal(false);
                  handlePrint(null);
                }}
                className="w-full text-left px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition-colors font-medium"
              >
                All agents
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowPrintAgentModal(false)}
              className="w-full px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Dropbox Folder Browser Modal */}
      <DropboxFolderBrowser
        showFolderBrowser={showFolderBrowser}
        setShowFolderBrowser={setShowFolderBrowser}
        selectedFile={selectedFile}
        currentPath={currentPath}
        setCurrentPath={setCurrentPath}
        dropboxFolders={dropboxFolders}
        loadingFolders={loadingFolders}
        fetchDropboxFolders={fetchDropboxFolders}
        setSelectedFolder={setSelectedFolder}
        handleConfirmUpload={handleConfirmUpload}
      />
    </div>
  );
};
export default TradeDetailsModal;
